import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Search, Phone, Mail, MessageSquare, FileText, Calendar, Camera,
  Clock, AlertCircle, CheckCircle, User, Building2, ChevronDown, ChevronUp,
  Pencil, Trash2, ClipboardList, X, Menu, PanelRightOpen, PanelLeftOpen,
  ExternalLink, MapPin, Tag, Info, Hash, Mic, Image as ImageIcon, Paperclip,
  Bot, UserCircle, ChevronRight, ChevronLeft, Smartphone, Sparkles, Loader2, PenTool, Plus, AlertTriangle,
  EyeOff, RotateCcw
} from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import Modal from '../components/Common/Modal';
import AudioPlayer from '../components/Common/AudioPlayer';
import AnfrageForm from '../components/Anfrage/AnfrageForm';
import SkizzenPad from '../components/Skizze/SkizzenPad';
import CounterProposalModal from '../components/Notifications/CounterProposalModal';
import TerminForm from '../components/Termin/TerminForm';
import NeueAnfrageModal from '../components/Anfrage/NeueAnfrageModal';
import apiClient from '../api/apiClient';
import { getAllAnfragen, getAnfrageById, getOffeneAktionen, updateAnfrage, deleteAnfrage, zusammenfassungAnfrage, bulkUpdateAnfragen, bulkDeleteAnfragen } from '../api/anfrageApi';
import { getAllBenutzer } from '../api/benutzerApi';
import { uploadFoto } from '../api/fotoApi';
import { createTermin } from '../api/termineApi';
import { emailAgentApi } from '../api/emailAgentApi';
import { kvaApi } from '../api/kvaApi';
import { notificationApi } from '../api/notificationApi';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAnfragenRefresh } from '../hooks/useDataRefresh';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// HeyKiki-Endkunden sehen keine Telefonassistent-Provenance-Felder in extrahierteDaten.
const HEYKIKI_HIDDEN_EXTRACTED_FIELDS = new Set([
  'quelle', 'source', 'agentId', 'agent_id', 'conversationId', 'conversation_id', 'callSuccessful', 'call_successful', 'callId'
]);

// Öffnet ein Dokument im Browser-Tab statt es herunterzuladen oder die PWA
// zu starten. Hintergrund: ein direkter `<a href="/api/dokumente/.../download" target="_blank">`
// schickt (a) keinen Bearer-Token mit (Endpoint braucht aber Auth) und (b) wenn
// die WerkPilot-PWA installiert ist, fängt sie alle URLs im eigenen Scope ab —
// auf macOS/iOS landet der Klick dann in der PWA, nicht im Browser. Pattern
// gleich zu DokumenteTab.jsx:handlePdfOpen — Fetch mit Auth → Blob → window.open
// auf eine `blob:`-URL, die NICHT im PWA-Scope liegt → Browser/PDF-Viewer übernimmt.
async function openDokumentInBrowser(dokumentId, fallbackName = 'Dokument') {
  try {
    const token = localStorage.getItem('token');
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const response = await fetch(`${apiUrl}/dokumente/download/${dokumentId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`Download fehlgeschlagen (${response.status})`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    // Blob-URL nach 60s revoken — gibt dem neuen Tab Zeit, das PDF zu laden.
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    console.error('[AnfrageDetail] Dokument öffnen fehlgeschlagen:', error);
    toast.error(`Dokument "${fallbackName}" konnte nicht geöffnet werden`);
  }
}

// Öffnet eine statisch gehostete PDF-Datei (aus /api/uploads/...) als Blob im
// Browser-Tab. Gleiche PWA-Scope-Problematik wie bei `openDokumentInBrowser`:
// ein nackter `<a href="/api/uploads/...">` würde unter der installierten PWA
// von dieser abgefangen werden. Auth-Header senden wir vorsorglich mit, falls
// der statische Upload-Pfad in Zukunft auth-geschützt wird.
async function openUploadPdfInBrowser(pdfPfad, fallbackName = 'Dokument') {
  if (!pdfPfad) {
    toast.error('Noch kein PDF vorhanden');
    return;
  }
  try {
    const token = localStorage.getItem('token');
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const response = await fetch(`${apiUrl}${pdfPfad}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
    });
    if (!response.ok) throw new Error(`Download fehlgeschlagen (${response.status})`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    console.error('[AnfrageDetail] Upload-PDF öffnen fehlgeschlagen:', error);
    toast.error(`${fallbackName} konnte nicht geöffnet werden`);
  }
}

// ── Config ──────────────────────────────────────────────────────────────────────

// Lucas/Husmann&Dreier-Feedback 2026-05-07: "Neu" → "Offen" auf Karten-Pill,
// damit der Pill mit dem Filter-Tab konsistent ist. DB-Status-Wert bleibt
// "neu" — nur das UI-Label wechselt.
const STATUS_CONFIG = {
  neu: { label: 'Offen', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  abgeschlossen: { label: 'Erledigt', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

const KATEGORIE_CONFIG = {
  'Termin': { icon: Calendar, color: 'text-purple-600 bg-purple-50' },
  'Angebot': { icon: FileText, color: 'text-blue-600 bg-blue-50' },
  'Info': { icon: Info, color: 'text-gray-600 bg-gray-50' },
  'Rückruf': { icon: Phone, color: 'text-green-600 bg-green-50' },
  'E-Mail': { icon: Mail, color: 'text-yellow-600 bg-yellow-50' }
};

const AGENT_STATUS_CONFIG = {
  NEU: { label: 'Neu', color: 'bg-gray-100 text-gray-600' },
  INFO_SAMMELN: { label: 'Info sammeln', color: 'bg-blue-100 text-blue-600' },
  WARTE_TERMIN_AUSWAHL: { label: 'Warte auf Termin', color: 'bg-purple-100 text-purple-600' },
  TERMIN_BESTAETIGT: { label: 'Termin bestätigt', color: 'bg-green-100 text-green-600' },
  WARTE_KVA_MANUELL: { label: 'KVA manuell', color: 'bg-orange-100 text-orange-600' },
  WARTE_KVA_BESTAETIGUNG: { label: 'KVA Bestätigung', color: 'bg-orange-100 text-orange-600' },
  KVA_ERSTELLT: { label: 'KVA erstellt', color: 'bg-teal-100 text-teal-600' },
  DATEN_AUFGENOMMEN: { label: 'Daten aufgenommen', color: 'bg-gray-100 text-gray-600' },
  MANUELL: { label: 'Manuell', color: 'bg-red-100 text-red-600' },
  WARTE_MANUELL: { label: 'Warte manuell', color: 'bg-red-100 text-red-600' },
  ANGEBOT_ANGENOMMEN: { label: 'Angenommen', color: 'bg-green-100 text-green-600' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-600' }
};

// ── Helpers ─────────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(d)) / (1000 * 60 * 60 * 24));
  if (days === 0) return `Heute, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  if (days === 1) return `Gestern, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  if (days > 1 && days < 7) return `vor ${days} Tagen`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatDuration(seconds) {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function detectChannel(anfrage) {
  if (!anfrage) return 'manuell';
  if (anfrage.whatsappKonversationen?.length > 0) return 'whatsapp';
  if (anfrage.emailKonversationen?.length > 0) return 'email';
  if (anfrage.callLogs?.length > 0) return 'telefon';
  // Fallback: Anfrage created from phone call but callLog not yet linked
  const quelle = anfrage.extrahierteDaten?.quelle;
  if (quelle && (quelle === 'Telefonat' || quelle.startsWith('Telefonat') || quelle === 'ElevenLabs' || quelle === 'telefonassistent')) return 'telefon';
  return 'manuell';
}

// Spezifische deutsche Labels für Keys aus extrahierteDaten.
// Was hier nicht steht, fällt auf den generischen Capitalize-Formatter zurück.
const FIELD_LABEL_OVERRIDES = {
  direction: 'Anruftyp',
};

// Wert-Transformer pro Key — für Enum-artige Felder, damit HeyKiki-Handwerker
// lesbare deutsche Begriffe sehen statt englischer Technik-Werte ('inbound' etc).
const FIELD_VALUE_MAPS = {
  direction: {
    outbound: 'Ausgehend',
    inbound: 'Eingehend',
  },
};

function formatFieldLabel(key) {
  if (FIELD_LABEL_OVERRIDES[key]) return FIELD_LABEL_OVERRIDES[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatFieldValue(key, value) {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  const map = FIELD_VALUE_MAPS[key];
  if (map) {
    const k = String(value).toLowerCase();
    if (map[k]) return map[k];
  }
  return String(value);
}

// ── Outbound-Call-Helpers ──────────────────────────────────────────────────────
// Mapping OutboundCall.anlassTyp → menschlich lesbares Label (DE).
const OUTBOUND_ANLASS_LABELS = {
  TERMIN_BESTAETIGUNG: 'Terminbestätigung',
  TERMIN_ERINNERUNG: 'Terminerinnerung',
  TERMIN_ABSAGE: 'Terminabsage',
  VERSPAETUNG: 'Verspätungsmeldung',
  TERMIN_GEGENVORSCHLAG: 'Terminvorschlag',
  WARTUNGSTERMIN: 'Wartungstermin',
  MAHNUNG_STUFE1: 'Mahnung 1',
  MAHNUNG_STUFE2: 'Mahnung 2',
  MAHNUNG_STUFE3: 'Mahnung 3',
  KVA_NACHFASSEN: 'KVA-Nachfass',
  MANUELL: 'Rückruf',
};

// Mapping OutboundCall.ergebnis → Badge-Konfig. tone = Tailwind-Pillenfarbe.
// Reihenfolge der Prio: spezifisches Outcome (TERMIN_BESTAETIGT etc.) sticht
// generisches Outcome (ERREICHT/MAILBOX/…) — der Caller entscheidet, was
// reingegeben wird, das Mapping bleibt 1:1.
const OUTBOUND_ERGEBNIS_BADGE = {
  TERMIN_BESTAETIGT:  { label: '✓ Termin bestätigt',     tone: 'bg-green-100 text-green-800 border-green-200' },
  TERMIN_ABGESAGT:    { label: '✗ Termin abgelehnt',     tone: 'bg-red-100 text-red-800 border-red-200' },
  ZAHLUNG_ZUGESAGT:   { label: '✓ Zahlung zugesagt',     tone: 'bg-green-100 text-green-800 border-green-200' },
  INTERESSE_BEKUNDET: { label: '✓ Interesse',            tone: 'bg-green-100 text-green-800 border-green-200' },
  KEIN_INTERESSE:     { label: '✗ Kein Interesse',       tone: 'bg-gray-100 text-gray-700 border-gray-200' },
  ABGELEHNT:          { label: '✗ Abgelehnt',            tone: 'bg-red-100 text-red-800 border-red-200' },
  ERREICHT:           { label: '✓ Kunde erreicht',       tone: 'bg-green-100 text-green-800 border-green-200' },
  MAILBOX:            { label: '⚠ Mailbox',              tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  BESETZT:            { label: '⚠ Besetzt',              tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  NICHT_ERREICHBAR:   { label: '⚠ Nicht erreichbar',     tone: 'bg-amber-100 text-amber-800 border-amber-200' },
};

// Wenn `ergebnis` noch null ist (Call läuft / fehlgeschlagen ohne Outcome),
// nutzen wir den OutboundCall.status als Fallback-Badge.
const OUTBOUND_STATUS_BADGE = {
  GEPLANT:         { label: '⏳ Geplant',         tone: 'bg-gray-100 text-gray-700 border-gray-200' },
  GESTARTET:       { label: '⏳ Läuft',           tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  VERBUNDEN:       { label: '⏳ Im Gespräch',     tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  ABGESCHLOSSEN:   { label: '· Abgeschlossen',    tone: 'bg-gray-100 text-gray-700 border-gray-200' },
  FEHLGESCHLAGEN:  { label: '✗ Fehlgeschlagen',   tone: 'bg-red-100 text-red-800 border-red-200' },
  ABGEBROCHEN:     { label: '✗ Abgebrochen',      tone: 'bg-red-100 text-red-800 border-red-200' },
};

function getOutboundBadge(outboundCall) {
  if (!outboundCall) return null;
  if (outboundCall.ergebnis && OUTBOUND_ERGEBNIS_BADGE[outboundCall.ergebnis]) {
    return OUTBOUND_ERGEBNIS_BADGE[outboundCall.ergebnis];
  }
  if (outboundCall.status && OUTBOUND_STATUS_BADGE[outboundCall.status]) {
    return OUTBOUND_STATUS_BADGE[outboundCall.status];
  }
  return null;
}

function getFotoUrl(foto) {
  const baseURL = import.meta.env.VITE_API_URL?.replace('/api', '') || '';
  if (foto.dateipfad?.startsWith('/api/uploads')) {
    return `${baseURL}${foto.dateipfad}`;
  }
  const filename = foto.dateipfad?.split('/').pop() || foto.dateiname;
  return `${baseURL}/api/uploads/${filename}`;
}

// ── Left Sidebar: Anfragen List ─────────────────────────────────────────────────

const SIDEBAR_PAGE_SIZE = 50;

// ── AktionenList ───────────────────────────────────────────────────────────────
// Rendert die „Offene Aktionen"-View in der linken Sidebar. Datenquelle:
// /anfragen/offene-aktionen (Notification-basierte pending actions, gleiche
// 4 Types wie in getAnfrageById). Klick → navigiert zur zugehörigen Anfrage,
// wo der TerminActionsBlock dann die Bestätigen/Ablehnen-Buttons zeigt.
const AKTION_TYPE_META = {
  TERMIN_BESTAETIGUNG_ANFRAGE: { label: 'Termin-Anfrage', accent: 'bg-blue-100 text-blue-700' },
  TERMIN_AENDERUNG_ANFRAGE:    { label: 'Termin-Änderung', accent: 'bg-amber-100 text-amber-700' },
  TERMIN_STORNIERUNG_ANFRAGE:  { label: 'Termin-Stornierung', accent: 'bg-red-100 text-red-700' },
  KVA_BESTAETIGUNG_ANFRAGE:    { label: 'KVA-Bestätigung', accent: 'bg-purple-100 text-purple-700' }
};

function formatRelativeAge(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} Std`;
  const d = Math.round(h / 24);
  if (d < 7) return `vor ${d} Tg`;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function AktionenList({ aktionen, loading, selectedId, onSelect }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }
  if (!aktionen || aktionen.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
        <CheckCircle className="w-10 h-10 text-green-300 mb-2" />
        <p className="text-sm font-medium text-text">Keine offenen Aktionen</p>
        <p className="text-xs text-text-muted mt-1">Alles bestätigt — gute Arbeit.</p>
      </div>
    );
  }
  return (
    <div>
      {aktionen.map(action => {
        const meta = AKTION_TYPE_META[action.type] || { label: 'Aktion', accent: 'bg-gray-100 text-gray-700' };
        const d = action.data || {};
        const terminLabel = d.datum
          ? `${d.datum}${d.uhrzeit ? ' um ' + d.uhrzeit : ''}`
          : '';
        const isActive = action.anfrageId === selectedId;
        const isAusgeblendet = !!action.ausgeblendet;
        return (
          <button
            key={action.id}
            onClick={() => onSelect(action.anfrageId)}
            className={`w-full text-left px-3 py-3 border-b border-border transition hover:bg-gray-50 group ${
              isActive ? 'bg-primary/5 border-l-2 border-l-primary' : 'border-l-2 border-l-transparent'
            } ${isAusgeblendet ? 'opacity-50' : ''}`}
          >
            <div className="flex items-start gap-2">
              {isAusgeblendet ? (
                <span className="flex h-2 w-2 mt-1.5 shrink-0 rounded-full bg-gray-400" />
              ) : (
                <span className="relative flex h-2 w-2 mt-1.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.accent}`}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-text-muted">{formatRelativeAge(action.createdAt)}</span>
                </div>
                <p className={`text-sm font-medium truncate ${isActive ? 'text-primary' : 'text-text'}`}>
                  {action.anfrage?.betreff || 'Anfrage'}
                </p>
                {action.anfrage?.kunde?.name && (
                  <p className="text-xs text-text-muted truncate mt-0.5">{action.anfrage.kunde.name}</p>
                )}
                {terminLabel && (
                  <p className="text-[11px] text-text-muted mt-0.5 truncate">{terminLabel}</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition shrink-0 mt-0.5" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// 24px-Avatar mit Initialen für die Anrufliste — Lucas/Husmann&Dreier-
// Feedback 2026-05-07: Zuständigkeit muss auf einen Blick erkennbar sein.
// Farbe ist deterministisch aus der Bearbeiter-ID gehasht, damit ein
// Mitarbeiter über alle Listen hinweg dieselbe Farbe behält. Ohne Bearbeiter:
// grauer "?"-Kreis.
const AVATAR_COLOR_PALETTE = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
];

function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(seed) {
  if (!seed) return AVATAR_COLOR_PALETTE[0];
  const str = String(seed);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_COLOR_PALETTE[Math.abs(hash) % AVATAR_COLOR_PALETTE.length];
}

function BearbeiterAvatar({ bearbeiter, size = 24 }) {
  const dim = `${size}px`;
  if (!bearbeiter) {
    return (
      <span
        className="rounded-full bg-gray-100 text-gray-400 flex items-center justify-center font-semibold shrink-0"
        style={{ width: dim, height: dim, fontSize: `${Math.round(size * 0.45)}px` }}
        title="Niemandem zugewiesen"
      >
        ?
      </span>
    );
  }
  const initials = getInitials(bearbeiter.name);
  const color = getAvatarColor(bearbeiter.id || bearbeiter.name);
  return (
    <span
      className={`rounded-full flex items-center justify-center font-semibold shrink-0 ${color}`}
      style={{ width: dim, height: dim, fontSize: `${Math.round(size * 0.42)}px` }}
      title={`Zugewiesen an ${bearbeiter.name}`}
    >
      {initials}
    </span>
  );
}

function AnfragenSidebar({ anfragen, selectedId, onSelect, searchTerm, onSearchChange, statusFilter, onStatusFilterChange, loading, onCollapse, benutzerList = [], bearbeiterFilter = 'alle', onBearbeiterFilterChange, currentUserId, showExtras = false, bulkEnabled = false, sortBy = 'newest', onSortByChange, onDeleteAnfrage, selectedIds, onToggleSelect, onToggleSelectAll, onClearSelection, onBulkStatusChange, onBulkDelete, bulkBusy = false, onNeueAnfrage, sidebarMode = 'anfragen', onSidebarModeChange, offeneAktionen = [], offeneAktionenLoading = false }) {
  const selectionSize = selectedIds?.size || 0;
  const [currentPage, setCurrentPage] = useState(1);
  // Lucas/Husmann&Dreier-Feedback 2026-05-07: erweiterte Filter (Bearbeiter
  // + Sortierung) hinter einen Toggle, damit der Filter-Stack nicht so
  // erschlagend wirkt. Default zu, öffnet beim ersten aktiven Extra-Filter.
  const [extraFilterOpen, setExtraFilterOpen] = useState(
    bearbeiterFilter !== 'alle' || (sortBy && sortBy !== 'newest')
  );
  const extraFilterCount =
    (bearbeiterFilter && bearbeiterFilter !== 'alle' ? 1 : 0) +
    (sortBy && sortBy !== 'newest' ? 1 : 0);
  const statusCounts = useMemo(() => {
    const counts = { alle: anfragen.length, neu: 0, offen: 0, erledigt: 0 };
    anfragen.forEach(a => {
      if (a.status === 'neu') { counts.neu++; counts.offen++; }
      if (a.status === 'in_bearbeitung') counts.offen++;
      if (a.status === 'abgeschlossen') counts.erledigt++;
    });
    return counts;
  }, [anfragen]);

  const filtered = useMemo(() => {
    let list = anfragen;
    if (statusFilter === 'offen') list = list.filter(a => a.status !== 'abgeschlossen');
    else if (statusFilter === 'neu') list = list.filter(a => a.status === 'neu');
    else if (statusFilter === 'erledigt') list = list.filter(a => a.status === 'abgeschlossen');
    // Bearbeiter-Filter
    if (bearbeiterFilter === 'me' && currentUserId) {
      list = list.filter(a => a.bearbeiterId === currentUserId);
    } else if (bearbeiterFilter === 'unassigned') {
      list = list.filter(a => !a.bearbeiterId);
    } else if (bearbeiterFilter !== 'alle') {
      list = list.filter(a => a.bearbeiterId === bearbeiterFilter);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter(a =>
        a.betreff?.toLowerCase().includes(s) ||
        a.kunde?.name?.toLowerCase().includes(s) ||
        a.nachricht?.toLowerCase().includes(s)
      );
    }
    const byDate = (field, dir) => (a, b) => {
      const av = new Date(a[field] || a.createdAt || 0).getTime();
      const bv = new Date(b[field] || b.createdAt || 0).getTime();
      return dir === 'asc' ? av - bv : bv - av;
    };
    const sorted = [...list];
    if (sortBy === 'oldest') sorted.sort(byDate('erstelltAm', 'asc'));
    else if (sortBy === 'lastUpdated') sorted.sort(byDate('updatedAt', 'desc'));
    else if (sortBy === 'firstUpdated') sorted.sort(byDate('updatedAt', 'asc'));
    else sorted.sort(byDate('erstelltAm', 'desc'));
    return sorted;
  }, [anfragen, statusFilter, bearbeiterFilter, searchTerm, currentUserId, sortBy]);

  // Reset Pagination auf Seite 1, sobald sich Such- oder Filter-Parameter ändern.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, bearbeiterFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / SIDEBAR_PAGE_SIZE));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const pageStart = (safePage - 1) * SIDEBAR_PAGE_SIZE;
  const paginated = filtered.slice(pageStart, pageStart + SIDEBAR_PAGE_SIZE);

  const visibleIds = filtered.map(a => a.id);
  const allVisibleChecked = visibleIds.length > 0 && visibleIds.every(id => selectedIds?.has(id));
  const showBulkBar = bulkEnabled && selectionSize > 0 && onBulkStatusChange;

  // Counter (Badge) zählt NUR echte offene Aktionen — ausgeblendete bleiben
  // ausgenommen, damit der Badge widerspiegelt „Action items, die der
  // Disponent noch anfassen muss".
  // Tab-Sichtbarkeit hängt dagegen am TOTAL inkl. ausgeblendeter: solange es
  // irgendeine Aktion (auch nur ausgegraute) gibt, bleibt der Tab erreichbar —
  // sonst könnte man ausgeblendete nicht mehr reaktivieren.
  const aktionenCount = (offeneAktionen || []).filter(a => !a.ausgeblendet).length;
  const totalAktionen = offeneAktionen?.length || 0;
  const isAktionenMode = sidebarMode === 'aktionen';

  // Auto-Switch zurück auf „Anfragen", wenn ÜBERHAUPT keine Aktionen mehr
  // existieren (auch keine ausgeblendeten) — sonst stünde der User auf einem
  // versteckten Tab.
  useEffect(() => {
    if (isAktionenMode && totalAktionen === 0 && onSidebarModeChange) {
      onSidebarModeChange('anfragen');
    }
  }, [totalAktionen, isAktionenMode, onSidebarModeChange]);

  return (
    <div className="flex flex-col h-full">
      {/* Header — mehr Innen-Padding gegen die abgehakte Optik
          (Lucas/Husmann&Dreier-Feedback 2026-05-07). */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-base font-semibold text-text">
            {isAktionenMode ? 'Offene Aktionen' : 'Anfragen'}
          </h2>
          <span className="text-sm text-text-muted">
            {isAktionenMode
              ? `${aktionenCount}`
              : `${filtered.length} von ${anfragen.length}`}
          </span>
          {onCollapse && (
            <button
              onClick={onCollapse}
              className="ml-auto p-1 hover:bg-gray-100 rounded-md transition"
              title="Sidebar einklappen"
            >
              <ChevronLeft className="w-4 h-4 text-text-muted" />
            </button>
          )}
        </div>

        {/* Tab-Switcher: Anfragen ↔ Offene Aktionen.
            Bleibt sichtbar, solange es überhaupt Aktionen gibt — auch wenn
            alle gerade ausgeblendet sind. Sonst könnte der Disponent eine
            ausgegraute Aktion nicht mehr reaktivieren. Verschwindet nur
            komplett, wenn 0 Aktionen (offen + ausgeblendet) übrig sind. */}
        {onSidebarModeChange && totalAktionen > 0 && (
          <div className="flex bg-gray-100 rounded-lg p-0.5 mb-2">
            <button
              onClick={() => onSidebarModeChange('anfragen')}
              className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition ${
                !isAktionenMode ? 'bg-white text-text shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Anfragen
            </button>
            <button
              onClick={() => onSidebarModeChange('aktionen')}
              className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition flex items-center justify-center gap-1 ${
                isAktionenMode ? 'bg-white text-text shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Aktionen die auf Bestätigung warten"
            >
              Aktionen
              {aktionenCount > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                  isAktionenMode ? 'bg-primary text-white' : 'bg-red-500 text-white'
                }`}>
                  {aktionenCount}
                </span>
              )}
            </button>
          </div>
        )}

        {showExtras && onNeueAnfrage && !isAktionenMode && (
          <button
            onClick={onNeueAnfrage}
            className="w-full mb-2 flex items-center justify-center gap-2 bg-primary text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-primary-dark transition"
          >
            <Plus className="w-4 h-4" />
            Neue Anfrage
          </button>
        )}
        {!isAktionenMode && (
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Suchen..."
            className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        )}
        {!isAktionenMode && (
        <div className="flex gap-1 flex-wrap">
          {/* Lucas/Husmann&Dreier-Feedback 2026-05-07: "Neu" raus, da
              Dreier nur zwischen Offen und Erledigt unterscheiden möchte.
              Status der einzelnen Anfrage bleibt 3-stufig in der DB
              (neu/in_bearbeitung/abgeschlossen) — UI-Filter ist flacher. */}
          {[
            { value: 'alle', label: 'Alle' },
            { value: 'offen', label: 'Offen', count: statusCounts.offen },
            { value: 'erledigt', label: 'Erledigt', count: statusCounts.erledigt },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => onStatusFilterChange(f.value)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition ${
                statusFilter === f.value
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
              {f.count !== undefined && <span className="ml-1 opacity-70">{f.count}</span>}
            </button>
          ))}
        </div>
        )}
        {/* Erweiterte Filter (Bearbeiter + Sortierung) hinter einem
            Toggle — Lucas/Husmann&Dreier-Feedback 2026-05-07. */}
        {!isAktionenMode && (onBearbeiterFilterChange || (showExtras && onSortByChange)) && (
          <>
            <button
              type="button"
              onClick={() => setExtraFilterOpen(v => !v)}
              className={`w-full mt-2 flex items-center justify-between px-3 py-1.5 rounded-lg text-sm font-medium transition border ${
                extraFilterCount > 0
                  ? 'bg-primary/5 border-primary/30 text-primary'
                  : 'bg-gray-50 border-border text-text-muted hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                Filter
                {extraFilterCount > 0 && (
                  <span className="bg-primary text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {extraFilterCount}
                  </span>
                )}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${extraFilterOpen ? 'rotate-180' : ''}`} />
            </button>
            {extraFilterOpen && (
              <div className="mt-2 p-3 bg-gray-50/70 rounded-lg border border-gray-100 space-y-2">
                {onBearbeiterFilterChange && (
                  <div>
                    <label className="text-[11px] font-semibold text-text-muted uppercase tracking-wide block mb-1">Mitarbeiter</label>
                    <select
                      value={bearbeiterFilter}
                      onChange={(e) => onBearbeiterFilterChange(e.target.value)}
                      className={`w-full px-2.5 py-1.5 text-sm rounded-lg border border-border bg-white focus:outline-none focus:ring-1 focus:ring-primary ${
                        bearbeiterFilter !== 'alle' ? 'border-primary text-primary font-medium' : ''
                      }`}
                    >
                      <option value="alle">Alle Mitarbeiter</option>
                      {currentUserId && <option value="me">Nur meine</option>}
                      <option value="unassigned">Nicht zugewiesen</option>
                      {benutzerList.length > 0 && (
                        <optgroup label="Mitarbeiter">
                          {benutzerList.map(b => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                )}
                {showExtras && onSortByChange && (
                  <div>
                    <label className="text-[11px] font-semibold text-text-muted uppercase tracking-wide block mb-1">Sortierung</label>
                    <select
                      value={sortBy}
                      onChange={(e) => onSortByChange(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-border bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="newest">Neueste zuerst</option>
                      <option value="oldest">Älteste zuerst</option>
                      <option value="lastUpdated">Zuletzt aktualisiert</option>
                      <option value="firstUpdated">Zuerst aktualisiert</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {!isAktionenMode && bulkEnabled && onToggleSelectAll && filtered.length > 0 && (
          <label className="mt-2 flex items-center gap-2 text-xs text-text-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allVisibleChecked}
              onChange={() => onToggleSelectAll(visibleIds)}
              className="rounded border-border text-primary focus:ring-primary"
            />
            {selectionSize > 0
              ? `${selectionSize} ausgewählt`
              : 'Alle sichtbaren auswählen'}
          </label>
        )}
      </div>

      {/* Bulk Action Bar (nur bei Auswahl + breiter Sidebar) */}
      {!isAktionenMode && showBulkBar && (
        <div className="px-3 py-2 border-b border-border bg-primary/5 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-primary">
              {selectionSize} {selectionSize === 1 ? 'Anfrage' : 'Anfragen'}
            </span>
            <button
              onClick={onClearSelection}
              className="text-[11px] text-text-muted hover:text-text transition"
            >
              Auswahl aufheben
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => onBulkStatusChange('in_bearbeitung')}
              disabled={bulkBusy}
              className="flex-1 min-w-[70px] flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-md transition disabled:opacity-50"
            >
              <Clock className="w-3 h-3" /> In Bearbeitung
            </button>
            <button
              onClick={() => onBulkStatusChange('abgeschlossen')}
              disabled={bulkBusy}
              className="flex-1 min-w-[70px] flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium bg-green-50 text-green-700 hover:bg-green-100 rounded-md transition disabled:opacity-50"
            >
              <CheckCircle className="w-3 h-3" /> Erledigt
            </button>
            <button
              onClick={() => onBulkStatusChange('neu')}
              disabled={bulkBusy}
              className="flex-1 min-w-[70px] flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md transition disabled:opacity-50"
            >
              <AlertCircle className="w-3 h-3" /> Neu
            </button>
            {onBulkDelete && (
              <button
                onClick={onBulkDelete}
                disabled={bulkBusy}
                className="flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition disabled:opacity-50"
                title="Auswahl löschen"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* List — Lucas/Husmann&Dreier-Feedback 2026-05-07: Listen-Items
          als richtige Cards mit Border, Schatten und Padding zwischen den
          Items. Übersichtlicher als die durchgehende Border-Bottom-Liste,
          analog zum HeyKiki-Reference-Layout. */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5 bg-gray-50/30">
        {isAktionenMode ? (
          <AktionenList
            aktionen={offeneAktionen}
            loading={offeneAktionenLoading}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ) : loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-8">Keine Anfragen gefunden</p>
        ) : (
          paginated.map(anfrage => {
            const sc = STATUS_CONFIG[anfrage.status] || STATUS_CONFIG.neu;
            const isActive = anfrage.id === selectedId;
            return (
              <div
                key={anfrage.id}
                className={`relative group rounded-lg border transition ${
                  isActive
                    ? 'bg-primary/5 border-primary shadow-sm'
                    : 'bg-white border-gray-200 hover:border-primary/40 hover:shadow-sm'
                }`}
              >
                {bulkEnabled && onToggleSelect && (
                  <label
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-3 left-3 z-[1] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(anfrage.id) || false}
                      onChange={() => onToggleSelect(anfrage.id)}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                  </label>
                )}
                <button
                  onClick={() => onSelect(anfrage.id)}
                  className={`w-full text-left px-3.5 py-3.5 ${bulkEnabled && onToggleSelect ? 'pl-10' : ''}`}
                >
                  <div className="flex items-start gap-2.5">
                    {/* Bearbeiter-Avatar links — auf einen Blick sichtbar wem
                        der Anruf zugeordnet ist. Bei !bearbeiter grauer "?"-
                        Kreis (Lucas/Husmann&Dreier-Feedback 2026-05-07). */}
                    <BearbeiterAvatar bearbeiter={anfrage.bearbeiter} size={28} />
                    <span className={`mt-0.5 text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${sc.color}`}>
                      {sc.label}
                    </span>
                    <div className="min-w-0 flex-1">
                      {/* Schrift +1 Stufe für Handwerker-Lesbarkeit (Lucas-Feedback 2026-05-07) */}
                      <p className={`text-base font-medium truncate flex items-center gap-1.5 ${
                        anfrage.istNotfall ? 'text-red-900' : (isActive ? 'text-primary' : 'text-text')
                      } ${showExtras ? 'pr-14' : ''}`}>
                        {anfrage.istNotfall && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-red-600 text-white rounded shrink-0"
                            title="Notdienst"
                          >
                            <AlertTriangle className="w-2.5 h-2.5" />
                            Notdienst
                          </span>
                        )}
                        <span className="truncate">
                          {(anfrage.betreff || '').replace(/^🚨\s*NOTFALL\s*—\s*/i, '').trim() || '—'}
                        </span>
                      </p>
                      {anfrage.kunde && (
                        <p className="text-sm text-text-muted truncate mt-0.5">{anfrage.kunde.name}</p>
                      )}
                      <div className="flex items-center flex-wrap gap-2 mt-1.5">
                        <span className="text-sm text-text-muted">{formatDate(anfrage.erstelltAm)}</span>
                        {anfrage._count?.emailKonversationen > 0 && (
                          <span className="text-sm text-gray-400 flex items-center gap-0.5">
                            <Mail className="w-3.5 h-3.5" />{anfrage._count.emailKonversationen}
                          </span>
                        )}
                        {anfrage._count?.fotos > 0 && (
                          <span className="text-sm text-gray-400 flex items-center gap-0.5">
                            <Camera className="w-3.5 h-3.5" />{anfrage._count.fotos}
                          </span>
                        )}
                        {anfrage.bearbeiter ? (
                          <span
                            className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium truncate max-w-[140px]"
                            title={`Zugewiesen an ${anfrage.bearbeiter.name}`}
                          >
                            {anfrage.bearbeiter.name}
                          </span>
                        ) : (
                          <span
                            className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium"
                            title="Niemandem zugewiesen"
                          >
                            Nicht zugewiesen
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
                {showExtras && (
                  <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                    {anfrage.kunde && (
                      <Link
                        to={`/kunden/${anfrage.kundeId}`}
                        onClick={(e) => e.stopPropagation()}
                        title={`Zum Kunden ${anfrage.kunde.name}`}
                        className="p-1.5 rounded-md hover:bg-primary/10 text-text-muted hover:text-primary transition"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    )}
                    {onDeleteAnfrage && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteAnfrage(anfrage); }}
                        title="Anfrage löschen"
                        className="p-1.5 rounded-md hover:bg-red-50 text-text-muted hover:text-red-600 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination-Footer — nur wenn mehr als eine Seite (nicht im Aktionen-Mode) */}
      {!isAktionenMode && totalPages > 1 && !loading && (
        <div className="border-t border-border px-3 py-2 flex items-center justify-between bg-gray-50/50">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className="p-1.5 rounded-md hover:bg-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Vorherige Seite"
          >
            <ChevronLeft className="w-4 h-4 text-text-muted" />
          </button>
          <span className="text-xs text-text-muted tabular-nums">
            Seite {safePage} / {totalPages} · {filtered.length} Anfragen
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className="p-1.5 rounded-md hover:bg-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Nächste Seite"
          >
            <ChevronRight className="w-4 h-4 text-text-muted" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Middle Column: Channel-Adaptive Content ──────────────────────────────────

function ChannelContent({ anfrage, channel, onSkizze }) {
  if (!anfrage) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Wähle eine Anfrage aus der Liste</p>
        </div>
      </div>
    );
  }

  switch (channel) {
    case 'whatsapp': return <WhatsAppView anfrage={anfrage} onSkizze={onSkizze} />;
    case 'email': return <EmailView anfrage={anfrage} onSkizze={onSkizze} />;
    case 'telefon': return <TelefonView anfrage={anfrage} onSkizze={onSkizze} />;
    default: return <ManuellView anfrage={anfrage} onSkizze={onSkizze} />;
  }
}

// ── Shared: Fotos & Dokumente Section (Kundendetail-Stil) ──────────────────────

function AnhangSection({ anfrage, onSkizze }) {
  const hasFotos = anfrage.fotos?.length > 0;
  // Dokumente bewusst NICHT hier rendern — redundant mit dem „Kostenvoranschläge"-
  // Abschnitt oben auf der Detailseite, der die zugehörigen KVA-PDFs bereits
  // verlinkt (User-Feedback 2026-04-22).
  if (!hasFotos) return null;

  return (
    <div className="border-t border-border bg-white">
      <div className="p-4 space-y-4">
        {/* Fotos */}
        {hasFotos && (
          <div>
            <p className="text-xs text-text-muted mb-2 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" />
              {anfrage.fotos.length} {anfrage.fotos.length === 1 ? 'Foto' : 'Fotos'}
            </p>
            <div className="flex gap-2 flex-wrap">
              {anfrage.fotos.map((foto) => {
                const url = getFotoUrl(foto);
                return (
                  <div key={foto.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border hover:border-primary transition group/foto">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full h-full"
                    >
                      <img
                        src={url}
                        alt={foto.beschreibung || 'Foto'}
                        className="w-full h-full object-cover"
                      />
                    </a>
                    {onSkizze && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSkizze(url);
                        }}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/foto:opacity-100 transition flex items-center justify-center shadow"
                        title="Skizzieren"
                      >
                        <PenTool className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── WhatsApp Chat View ──────────────────────────────────────────────────────────

function WhatsAppView({ anfrage, onSkizze }) {
  const messagesEndRef = useRef(null);
  const allMessages = useMemo(() => {
    const msgs = [];
    (anfrage.whatsappKonversationen || []).forEach(conv => {
      (conv.nachrichten || []).forEach(n => {
        msgs.push({ ...n, konversationId: conv.id, phoneNumber: conv.phoneNumber });
      });
    });
    return msgs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [anfrage.whatsappKonversationen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length]);

  if (allMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        <div className="text-center">
          <Smartphone className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine WhatsApp-Nachrichten</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-green-50/50 flex items-center gap-2">
        <Smartphone className="w-4 h-4 text-green-600" />
        <span className="text-sm font-medium text-green-800">WhatsApp Konversation</span>
        <span className="text-xs text-green-600 ml-auto">{allMessages.length} Nachrichten</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f0f2f5]">
        {allMessages.map((msg, idx) => {
          const isOutgoing = msg.richtung === 'ausgehend';
          return (
            <div key={msg.id || idx} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-xl px-3 py-2 shadow-sm ${
                  isOutgoing
                    ? 'rounded-br-sm text-white'
                    : 'bg-white rounded-bl-sm text-text'
                }`}
                style={isOutgoing ? { backgroundColor: 'var(--primary)' } : undefined}
              >
                {/* Inline-Medien (Bilder / Dokumente) */}
                {msg.mediaUrls && Array.isArray(msg.mediaUrls) && msg.mediaUrls.length > 0 && msg.mediaUrls[0]?.chatImageId && (
                  <div className="mb-1.5 flex flex-wrap gap-1.5">
                    {msg.mediaUrls.map((media, mIdx) => {
                      if (!media.chatImageId) return null;
                      const isImage = media.mimeType?.startsWith('image/');
                      return isImage ? (
                        <a key={mIdx} href={`/api/chat/images/${media.chatImageId}`} target="_blank" rel="noopener noreferrer">
                          <img
                            src={`/api/chat/images/${media.chatImageId}`}
                            alt={media.dateiname || 'WhatsApp-Bild'}
                            className="max-w-[200px] max-h-[150px] rounded object-cover cursor-pointer hover:opacity-90"
                          />
                        </a>
                      ) : (
                        <a key={mIdx} href={`/api/chat/images/${media.chatImageId}`} target="_blank" rel="noopener noreferrer"
                           className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${isOutgoing ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-text hover:bg-gray-200'}`}>
                          <FileText className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[150px]">{media.dateiname || 'Dokument'}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
                {(() => {
                  const toolCalls = msg.toolCalls && Array.isArray(msg.toolCalls) ? msg.toolCalls : [];
                  if (toolCalls.length === 0) return null;
                  return (
                    <details className={`mb-1.5 border-b pb-1 ${isOutgoing ? 'border-white/20' : 'border-gray-200'}`}>
                      <summary className={`text-[10px] cursor-pointer flex items-center gap-1 ${isOutgoing ? 'text-white/70' : 'text-text-muted'}`}>
                        <Bot className="w-3 h-3" /> {toolCalls.length} Tool-Call{toolCalls.length > 1 ? 's' : ''}
                      </summary>
                      <div className="mt-1 space-y-1">
                        {toolCalls.map((tc, tIdx) => {
                          let toolName = tc.name || '';
                          let success = null;
                          let resultPreview = '';
                          try {
                            const content = typeof tc.content === 'string' ? JSON.parse(tc.content) : tc.content;
                            success = content?.success;
                            resultPreview = content?.message || content?.error || JSON.stringify(content).substring(0, 120);
                          } catch { resultPreview = String(tc.content || '').substring(0, 120); }
                          return (
                            <div key={tIdx} className={`text-[10px] rounded px-1.5 py-0.5 ${isOutgoing ? 'bg-white/10 text-white/80' : 'bg-gray-50 text-text-muted'}`}>
                              {toolName && <span className="font-semibold">{toolName}: </span>}
                              {success === true && <span className={isOutgoing ? 'text-green-200' : 'text-green-600'}>OK</span>}
                              {success === false && <span className={isOutgoing ? 'text-red-200' : 'text-red-600'}>FEHLER</span>}
                              {' '}<span className="font-mono opacity-80">{resultPreview}</span>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  );
                })()}
                <p className="text-sm whitespace-pre-wrap break-words">{msg.inhalt}</p>
                <div className={`flex items-center gap-1 mt-1 ${isOutgoing ? 'justify-end' : ''}`}>
                  <span className={`text-[10px] ${isOutgoing ? 'text-white/60' : 'text-text-muted'}`}>
                    {new Date(msg.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {isOutgoing && msg.messageStatus && (
                    <span className={`text-[10px] ${isOutgoing ? 'text-white/60' : 'text-text-muted'}`}>
                      {msg.messageStatus === 'read' ? '✓✓' : msg.messageStatus === 'delivered' ? '✓✓' : '✓'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Fotos & Dokumente */}
      <AnhangSection anfrage={anfrage} onSkizze={onSkizze} />
    </div>
  );
}

// ── Email Thread View ───────────────────────────────────────────────────────────

function EmailView({ anfrage, onSkizze }) {
  const [expandedEmails, setExpandedEmails] = useState({});
  const emails = useMemo(() =>
    [...(anfrage.emailKonversationen || [])].sort((a, b) => new Date(a.erstelltAm) - new Date(b.erstelltAm)),
    [anfrage.emailKonversationen]
  );

  // Expand the last email by default
  useEffect(() => {
    if (emails.length > 0) {
      setExpandedEmails({ [emails[emails.length - 1].id]: true });
    }
  }, [emails]);

  if (emails.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        <div className="text-center">
          <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine E-Mail-Konversationen</p>
        </div>
      </div>
    );
  }

  const toggleEmail = (id) => setExpandedEmails(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-blue-50/50 flex items-center gap-2">
        <Mail className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-medium text-blue-800">E-Mail Verlauf</span>
        <span className="text-xs text-blue-600 ml-auto">{emails.length} E-Mails</span>
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-y-auto">
        {emails.map((email, idx) => {
          const isExpanded = expandedEmails[email.id];
          const isIncoming = email.richtung === 'EINGEHEND';
          return (
            <div key={email.id} className={`border-b border-border ${isExpanded ? 'bg-white' : 'bg-gray-50/50'}`}>
              <button
                onClick={() => toggleEmail(email.id)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    isIncoming ? 'bg-blue-100 text-blue-600' : 'bg-primary/10 text-primary'
                  }`}>
                    {isIncoming ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {isIncoming ? email.absender : 'Gesendet'}
                      </span>
                      <span className="text-[11px] text-text-muted shrink-0">{formatDateTime(email.erstelltAm)}</span>
                    </div>
                    <p className="text-xs text-text-muted truncate">{email.betreff}</p>
                    {!isExpanded && (
                      <p className="text-xs text-text-muted truncate mt-0.5">
                        {(email.inhaltBereinigt || email.inhalt || '').substring(0, 120)}...
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 ml-2">
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
                  </div>
                </div>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-1">
                  <div className="text-xs text-text-muted mb-2 space-y-0.5">
                    <p>Von: {email.absender}</p>
                    <p>An: {email.empfaenger}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-wrap leading-relaxed border border-border">
                    {email.inhaltBereinigt || email.inhalt}
                  </div>
                  {email.anhaenge > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-text-muted">
                      <Paperclip className="w-3 h-3" />
                      {email.anhaenge} Anhang/Anhänge
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fotos & Dokumente */}
      <AnhangSection anfrage={anfrage} onSkizze={onSkizze} />
    </div>
  );
}

// ── Telefon View with Transcript + Audio ────────────────────────────────────────

// Parse "Assistent: text\nAnrufer: text" string transcripts into structured messages
function parseTranscriptString(text) {
  if (!text || typeof text !== 'string') return null;
  const lines = text.split('\n');
  const messages = [];
  const speakerPattern = /^(Assistent|Anrufer|AI|User|Bot|Assistant|KI|Kunde|Agent):\s*(.*)/i;

  for (const line of lines) {
    const match = line.match(speakerPattern);
    if (match) {
      const speaker = match[1].toLowerCase();
      const isAssistant = ['assistent', 'ai', 'bot', 'assistant', 'ki', 'agent'].includes(speaker);
      messages.push({ role: isAssistant ? 'assistant' : 'user', content: match[2] });
    } else if (line.trim() && messages.length > 0) {
      // Continuation of previous message
      messages[messages.length - 1].content += '\n' + line;
    }
  }
  return messages.length >= 2 ? messages : null;
}

function TranscriptBubbles({ messages, fullWidth = false, assistantLabel = 'KI-Assistent', assistantAvatar = null }) {
  // Parent-Container (TelefonView, flex-1 overflow-y-auto) scrollt bereits für
  // BEIDE Org-Typen — ein eigenes max-h/overflow hier wäre ein doppelter
  // Scroll-Container: das Transkript würde auf 384px abgeschnitten und es
  // entstünde ein großer Freiraum bis zum Aufzeichnungs-Footer. Daher KEIN
  // max-h, weder HeyKiki noch Standalone. fullWidth steuert nur noch Bubble-
  // Breite/Padding.
  // assistantLabel: bei HeyKiki-Orgs „Kiki", bei Standalone „KI-Assistent".
  // assistantAvatar: optionales Bild (z.B. /heykiki-chat-logo.png), das oben
  // rechts an die Sprechblase überlappt — visuelle Identität für Kiki.
  return (
    <div className={`space-y-3.5 ${assistantAvatar ? 'pr-4 pt-3' : ''}`}>
      {messages.map((entry, idx) => {
        const isAssistant = entry.role === 'assistant' || entry.speaker === 'assistant';
        return (
          <div key={idx} className={`flex ${isAssistant ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`${fullWidth ? 'max-w-[97%]' : 'max-w-[90%]'} relative rounded-xl px-4 py-3 shadow-sm ${
                isAssistant
                  ? 'rounded-br-sm text-white'
                  : 'bg-white border border-border rounded-bl-sm'
              }`}
              style={isAssistant ? { backgroundColor: 'var(--primary)' } : undefined}
            >
              {/* Kiki-Avatar oben rechts an der Sprechblase — nur bei
                  Assistant-Bubbles, leicht überlappend für visuelle Identität. */}
              {isAssistant && assistantAvatar && (
                <img
                  src={assistantAvatar}
                  alt={assistantLabel}
                  className="absolute -top-2.5 -right-2.5 w-8 h-8 rounded-full border-2 border-white shadow-sm bg-white object-cover"
                />
              )}
              {/* Lucas/Husmann&Dreier-Feedback 2026-05-07: Schriftgrößen
                  hochgezogen, weil Handwerker als Zielgruppe lesen müssen
                  und kleine Schrift schwer entzifferbar ist. */}
              <p className={`text-xs font-medium mb-1 ${isAssistant ? 'text-white/80' : 'text-text-muted'}`}>
                {isAssistant ? assistantLabel : 'Anrufer'}
              </p>
              <p className="text-base leading-relaxed">{entry.content || entry.text || entry.message}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TelefonView({ anfrage, onSkizze }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const callLogs = anfrage.callLogs || [];
  // Ältester Call = Erstkontakt — Aufnahme bleibt als Sticky-Footer dauerhaft
  // verfügbar (der ist meistens das Original-Anliegen). callLogs ist
  // startedAt:'desc' → letzter im Array = ältester Call.
  const oldestCallId = callLogs.length > 0 ? callLogs[callLogs.length - 1].id : null;
  const oldestCallWithRecording = [...callLogs].reverse().find(c => c.recordingUrl);

  // Fallback: show Anfrage message + extrahierteDaten when no callLogs found
  // (e.g. channel detected via extrahierteDaten.quelle but callLog not linked)
  if (callLogs.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 py-3 border-b border-border bg-purple-50/50 flex items-center gap-2">
          <Phone className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-purple-800">Telefonat</span>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {anfrage.nachricht && (
            <div>
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Nachricht</p>
              <div className="bg-white rounded-lg border border-border p-4">
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{anfrage.nachricht}</p>
              </div>
            </div>
          )}
          {anfrage.extrahierteDaten && Object.keys(anfrage.extrahierteDaten).length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Extrahierte Daten</p>
              <div className="bg-white rounded-lg border border-border p-4 space-y-2">
                {Object.entries(anfrage.extrahierteDaten)
                  .filter(([k]) => !['_', 'messageId', 'threadId', 'callId'].some(x => k.startsWith(x)))
                  .filter(([k]) => !(isHeykikiOrg && HEYKIKI_HIDDEN_EXTRACTED_FIELDS.has(k)))
                  .map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2">
                      <span className="text-xs font-medium text-text-muted w-32 shrink-0">{formatFieldLabel(key)}:</span>
                      <span className="text-sm text-text">
                        {formatFieldValue(key, value)}
                      </span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>
        <AnhangSection anfrage={anfrage} onSkizze={onSkizze} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-purple-50/50 flex items-center gap-2">
        <Phone className="w-4 h-4 text-purple-600" />
        <span className="text-sm font-medium text-purple-800">Telefonanrufe</span>
        <span className="text-xs text-purple-600 ml-auto">{callLogs.length} {callLogs.length === 1 ? 'Anruf' : 'Anrufe'}</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {callLogs.map((call) => {
          // Try to parse string transcript into structured messages for bubble display
          const parsedTranscript = typeof call.transcript === 'string'
            ? parseTranscriptString(call.transcript)
            : Array.isArray(call.transcript)
              ? call.transcript
              : null;

          return (
            <div key={call.id} className="border-b border-border p-4">
              {/* Call meta */}
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  call.direction === 'outbound' ? 'bg-primary/10 text-primary' : 'bg-purple-100 text-purple-600'
                }`}>
                  <Phone className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  {(() => {
                    // Kundenname bevorzugt anzeigen: zuerst am CallLog selbst, dann
                    // Fallback auf die Anfrage-Kunde (wenn Telefonnummer matched).
                    const number = call.callerNumber || call.calledNumber || null;
                    const normalizedCall = number ? number.replace(/\D/g, '').slice(-9) : '';
                    const anfrageTelDigits = (anfrage.kunde?.telefon || '').replace(/\D/g, '').slice(-9);
                    const matchesAnfrageKunde = anfrage.kunde?.name && anfrageTelDigits && normalizedCall && anfrageTelDigits === normalizedCall;
                    const kundenName = call.kunde?.name || (matchesAnfrageKunde ? anfrage.kunde.name : null);
                    const richtung = call.direction === 'outbound' ? 'Ausgehend' : 'Eingehend';
                    return (
                      <p className="text-sm font-medium">
                        {richtung}: {kundenName ? (
                          <>
                            {kundenName}
                            {number && number !== 'web-widget' && (
                              <span className="text-text-muted font-normal">
                                {' ('}
                                <a href={`tel:${number}`} className="hover:text-primary">
                                  {number}
                                </a>
                                {')'}
                              </span>
                            )}
                          </>
                        ) : (number || 'Unbekannt')}
                      </p>
                    );
                  })()}
                  <div className="flex items-center gap-2 text-xs text-text-muted flex-wrap">
                    <span>{formatDateTime(call.startedAt)}</span>
                    <span>{formatDuration(call.durationSeconds)}</span>
                    {call.direction === 'outbound' && call.outboundCall?.anlassTyp && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary border border-primary/20">
                        {OUTBOUND_ANLASS_LABELS[call.outboundCall.anlassTyp] || call.outboundCall.anlassTyp}
                      </span>
                    )}
                    {call.direction === 'outbound' && (() => {
                      const badge = getOutboundBadge(call.outboundCall);
                      if (!badge) return null;
                      return (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${badge.tone}`}>
                          {badge.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Zusammenfassung + Offene/Durchgeführte Aktionen sind jetzt in der
                  rechten Sidebar (DetailPanel) — siehe Task 2026-04-22. */}

              {/* Transcript as chat bubbles */}
              {call.transcript && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-text-muted uppercase mb-2">Gesprächsverlauf</p>
                  {parsedTranscript && parsedTranscript.length > 0 ? (
                    <TranscriptBubbles
                      messages={parsedTranscript}
                      fullWidth={isHeykikiOrg}
                      assistantLabel={isHeykikiOrg ? 'Kiki' : 'KI-Assistent'}
                      assistantAvatar={isHeykikiOrg ? '/heykiki-chat-logo.png' : null}
                    />
                  ) : typeof call.transcript === 'string' ? (
                    <p className="text-sm whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{call.transcript}</p>
                  ) : (
                    <pre className="text-xs whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{JSON.stringify(call.transcript, null, 2)}</pre>
                  )}
                </div>
              )}

              {/* Audio-Player pro Call (H&D-Feedback 2026-05-19) — Folge-Calls
                  (Outbound-Bestätigungen etc.) bekommen ihren Player direkt
                  unter dem Transkript. Der ÄLTESTE Call (Erstkontakt) wird
                  separat als Sticky-Footer ganz unten gerendert, damit er
                  jederzeit erreichbar bleibt — siehe nach dem Calls-Loop. */}
              {call.recordingUrl && call.id !== oldestCallId && (
                <div>
                  <p className="text-[10px] font-semibold text-text-muted uppercase mb-1.5 tracking-wide">Aufzeichnung</p>
                  <AudioPlayer src={call.recordingUrl} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fotos & Dokumente */}
      <AnhangSection anfrage={anfrage} onSkizze={onSkizze} />

      {/* Sticky Audio-Footer für den ÄLTESTEN Call (Erstkontakt) —
          dauerhaft erreichbar, auch nach langem Scroll durch Transkripte.
          Folge-Calls haben ihren eigenen Inline-Player oben. */}
      {oldestCallWithRecording && (
        <div className="border-t border-border bg-white/95 backdrop-blur p-3 shrink-0">
          <p className="text-[10px] font-semibold text-text-muted uppercase mb-1.5 tracking-wide">Aufzeichnung</p>
          <AudioPlayer src={oldestCallWithRecording.recordingUrl} />
        </div>
      )}
    </div>
  );
}

// ── TerminActionsBlock ─────────────────────────────────────────────────────────
// Zeigt die Benachrichtigungs-Aktionen (Bestätigen / Alternative / Ablehnen)
// direkt in der Anfragedetailseite unterhalb des Transkripts. Löst identische
// Aktionen aus wie die NotificationCenter-Buttons (POST /notifications/:id/...)
// mode (Lucas/Husmann&Dreier-Feedback 2026-05-07):
//   'pending'  → nur offene Aktionen (Aktionen-Tab)
//   'completed' → nur durchgeführte Aktionen (Verlauf-Tab)
//   undefined  → beide (legacy)
function TerminActionsBlock({ anfrage, onActionComplete, mode }) {
  const navigate = useNavigate();
  const [processingId, setProcessingId] = useState(null);
  const [counterPropAction, setCounterPropAction] = useState(null);
  const [counterPropLoading, setCounterPropLoading] = useState(false);
  // Google-Maps-Preview beim Hover über die Einsatzort-Adresse — gleiche
  // Mechanik wie in Kundendetail.jsx (Mouse-Position + Viewport-Clamping +
  // Grace-Period beim Verlassen, damit der User in das Popup wechseln kann).
  const [mapPreview, setMapPreview] = useState({ open: false, top: 0, left: 0, address: '' });
  const mapPreviewTimeout = useRef(null);
  const handleAddressMouseEnter = (e, address) => {
    if (!address) return;
    if (mapPreviewTimeout.current) clearTimeout(mapPreviewTimeout.current);
    const mouseX = e.clientX;
    const mouseY = e.clientY;
    mapPreviewTimeout.current = setTimeout(() => {
      const popupWidth = 320;
      const popupHeight = 280;
      let left = mouseX + 15;
      let top = mouseY + 15;
      if (left + popupWidth > window.innerWidth - 10) left = mouseX - popupWidth - 15;
      if (top + popupHeight > window.innerHeight - 10) top = mouseY - popupHeight - 15;
      setMapPreview({ open: true, top, left, address });
    }, 400);
  };
  const handleAddressMouseLeave = () => {
    if (mapPreviewTimeout.current) clearTimeout(mapPreviewTimeout.current);
    mapPreviewTimeout.current = setTimeout(() => {
      setMapPreview(prev => ({ ...prev, open: false }));
    }, 300);
  };
  const handleMapMouseEnter = () => {
    if (mapPreviewTimeout.current) clearTimeout(mapPreviewTimeout.current);
  };
  const handleMapMouseLeave = () => {
    setMapPreview(prev => ({ ...prev, open: false }));
  };
  useEffect(() => () => {
    if (mapPreviewTimeout.current) clearTimeout(mapPreviewTimeout.current);
  }, []);
  // Container-Width-Tracking, um die „Wartet auf Bestätigung"-Pill bei wenig
  // Platz auszublenden — sonst quetscht sie den Titel der Action-Card.
  const containerRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);
  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsCompact(entry.contentRect.width < 440);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  // Optionen-Aufklappfeld pro Action: erlaubt vor dem Bestätigen, die Termin-
  // Dauer (Default 60) und einen zuständigen Mitarbeiter zu setzen — sonst
  // müssten Anwender nach dem Klick im Kalender manuell nachjustieren.
  // Pro Action separat, weil mehrere Aktionen parallel offen sein können.
  const [expandedOptionsId, setExpandedOptionsId] = useState(null);
  const [optionsByAction, setOptionsByAction] = useState({});
  const [benutzerList, setBenutzerList] = useState([]);
  const [kategorienList, setKategorienList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAllBenutzer({ aktiv: 'true' }).catch(() => []),
      // Termin-Kategorien: best-effort Load — ist die Org-Liste leer oder das
      // Modul nicht verfügbar, blendet sich das Dropdown unten einfach aus.
      apiClient.get('/termin-kategorien').then(r => r.data).catch(() => [])
    ]).then(([bens, kats]) => {
      if (cancelled) return;
      setBenutzerList(Array.isArray(bens) ? bens : []);
      setKategorienList(Array.isArray(kats) ? kats.filter(k => k.aktiv !== false) : []);
    });
    return () => { cancelled = true; };
  }, []);

  const setOptionFor = (actionId, patch) => {
    setOptionsByAction(prev => ({ ...prev, [actionId]: { ...(prev[actionId] || {}), ...patch } }));
  };

  // Kategorie-Auswahl füllt Dauer + Bearbeiter mit den Default-Werten der Kategorie,
  // sofern noch nichts Eigenes gesetzt ist. User kann beide trotzdem überschreiben.
  const applyKategorie = (actionId, kategorieId) => {
    const cur = optionsByAction[actionId] || {};
    if (!kategorieId) {
      setOptionFor(actionId, { kategorieId: null });
      return;
    }
    const kat = kategorienList.find(k => k.id === kategorieId);
    if (!kat) return;
    setOptionFor(actionId, {
      kategorieId,
      dauerMinuten: cur.dauerMinuten ?? kat.defaultDauerMinuten,
      bearbeiterId: cur.bearbeiterId ?? kat.defaultBearbeiterId ?? null,
    });
  };

  // Auto-Vorbelegung aus KI-Vorschlag: Wenn der ElevenLabs-Agent beim bookAppointment-
  // Call eine Kategorie erkannt hat, schreibt das Backend `kategorieId/Name/Dauer/
  // BearbeiterId` in `notification.data`. Hier ziehen wir das in `optionsByAction`
  // — aber NUR, wenn der Admin für diese Action noch nichts manuell gesetzt hat
  // (sonst würde ein erneutes Daten-Reload Admin-Edits überschreiben).
  // Trigger: pendingActions (über anfrage.pendingActions) und kategorienList laden
  // asynchron, deshalb beobachten wir beide.
  useEffect(() => {
    if (!Array.isArray(anfrage?.pendingActions) || anfrage.pendingActions.length === 0) return;
    if (!Array.isArray(kategorienList) || kategorienList.length === 0) return;

    setOptionsByAction(prev => {
      let changed = false;
      const next = { ...prev };
      for (const action of anfrage.pendingActions) {
        if (next[action.id]) continue; // Admin hat hier schon etwas gesetzt — nicht überschreiben
        const sugId = action?.data?.kategorieId;
        if (!sugId) continue;
        // Sicherheits-Check: empfohlene Kategorie existiert noch in der Org?
        const kat = kategorienList.find(k => k.id === sugId);
        if (!kat) continue;
        next[action.id] = {
          kategorieId: sugId,
          dauerMinuten: action.data.kategorieDauerMinuten ?? kat.defaultDauerMinuten,
          bearbeiterId: action.data.kategorieBearbeiterId ?? kat.defaultBearbeiterId ?? null,
        };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [anfrage?.pendingActions, kategorienList]);

  // Datenquelle: vom Backend mitgeliefertes `pendingActions`-Array
  // (Notifications mit aktion='bestaetigen_oder_ablehnen' zu dieser anfrageId).
  // Fallback auf altes Schema via terminBestaetigung für den Fall dass beides
  // gleichzeitig vorkommt.
  const pending = [
    ...(anfrage.pendingActions || []),
    ...(anfrage.terminBestaetigung || [])
      .filter(tb => tb.status === 'PENDING' && tb.notificationId)
      .map(tb => ({
        id: tb.notificationId,
        type: 'TERMIN_BESTAETIGUNG_ANFRAGE',
        data: { datum: tb.datum ? new Date(tb.datum).toLocaleDateString('de-DE') : null, uhrzeit: tb.uhrzeit, beschreibung: tb.beschreibung, typ: 'TERMIN_BUCHUNG' }
      }))
  ];
  const completedAll = anfrage.completedActions || [];
  // mode-Filter: in 'pending' verstecken wir completed, in 'completed' verstecken wir pending
  const showPending = mode !== 'completed';
  const showCompleted = mode !== 'pending';
  const completed = showCompleted ? completedAll : [];
  const visiblePending = showPending ? pending : [];
  if (visiblePending.length === 0 && completed.length === 0) return null;

  // Statt einer kompletten Page-Reload (baut die gesamte Anfrage-Detail-Seite
  // neu auf und flackert sichtbar) triggern wir nur das gezielte Nachladen
  // der Anfrage-Daten via `onActionComplete`. Das Parent-Component ruft
  // `loadAnfrageDetail(selectedId)` + `loadAnfragen()` auf, wodurch sich
  // `pendingActions`/`completedActions` aktualisieren, ohne dass Scroll-
  // Position, offene Lightboxes etc. verloren gehen.
  const reload = () => { if (onActionComplete) onActionComplete(); };

  const getTypeLabel = (t) => {
    if (t === 'TERMIN_STORNIERUNG_ANFRAGE') return { label: 'Termin-Stornierung', confirm: 'Stornieren', showAlternative: false, showEdit: false };
    if (t === 'TERMIN_AENDERUNG_ANFRAGE') return { label: 'Termin-Änderung', confirm: 'Bestätigen', showAlternative: true, showEdit: false };
    if (t === 'KVA_BESTAETIGUNG_ANFRAGE') return { label: 'KVA-Bestätigung', confirm: 'Bestätigen', showAlternative: false, showEdit: true };
    return { label: 'Termin-Anfrage', confirm: 'Bestätigen', showAlternative: true, showEdit: false };
  };

  const handleEditKva = async (action) => {
    // 1. Direkt-ID aus der Notification.
    const kvaId = action.data?.kvaId;
    if (kvaId) {
      navigate(`/kostenvoranschlaege/${kvaId}/bearbeiten`);
      return;
    }
    // 2. Fallback: Jüngster KVA dieser Anfrage (per erstelltAm desc sortiert).
    const recentKvaId = anfrage.kostenvoranschlaege?.[0]?.id;
    if (recentKvaId) {
      navigate(`/kostenvoranschlaege/${recentKvaId}/bearbeiten`);
      return;
    }
    // 3. Letzter Fall: Nur kvaBestaetigung existiert, noch kein Kostenvoran-
    //    schlag. Bei „Bearbeiten" erstellen wir jetzt serverseitig den Entwurf
    //    aus der Bestätigung (via vorhandenem `approve-as-draft`-Endpoint) und
    //    springen direkt in die Bearbeitung. Vorher landete der User auf der
    //    Zwischenseite /kva-bestaetigung/:id — das fühlte sich wie ein Klick
    //    ins Leere an.
    const bestaetigungId = action.data?.kvaBestaetungId || action.data?.kvaBestaetigungId;
    if (!bestaetigungId) {
      navigate('/kostenvoranschlaege');
      return;
    }
    setProcessingId(action.id);
    try {
      const res = await apiClient.post(`/kva-bestaetigung/${bestaetigungId}/approve-as-draft`);
      const newKvaId = res?.data?.kva?.id;
      if (newKvaId) {
        navigate(`/kostenvoranschlaege/${newKvaId}/bearbeiten`);
      } else {
        // Fallback: wenn der Server doch keinen KVA anlegen konnte, zur
        // Zwischenseite — besser als stiller Fehler.
        navigate(`/kva-bestaetigung/${bestaetigungId}`);
      }
    } catch (e) {
      toast.error('KVA-Entwurf konnte nicht erstellt werden: ' + (e.response?.data?.error || e.message));
      navigate(`/kva-bestaetigung/${bestaetigungId}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleConfirm = async (action) => {
    setProcessingId(action.id);
    try {
      // Optionen aus dem Aufklappfeld nur mitsenden, wenn explizit gesetzt —
      // sonst greift im Backend der Default (60 Min, kein Mitarbeiter).
      const opts = optionsByAction[action.id] || {};
      const body = {};
      if (opts.dauerMinuten && Number.isFinite(Number(opts.dauerMinuten))) {
        body.dauerMinuten = Number(opts.dauerMinuten);
      }
      if (opts.bearbeiterId) {
        body.bearbeiterId = opts.bearbeiterId;
      }
      if (opts.kategorieId) {
        body.kategorieId = opts.kategorieId;
      }
      await apiClient.post(`/notifications/${action.id}/confirm`, body);
      toast.success('Bestätigt. Der Kunde wird benachrichtigt.');
      reload();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.error || e.message));
    } finally { setProcessingId(null); }
  };

  const handleReject = async (action) => {
    setProcessingId(action.id);
    try {
      await apiClient.post(`/notifications/${action.id}/reject`);
      toast.success('Abgelehnt. Der Kunde wird benachrichtigt.');
      reload();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.error || e.message));
    } finally { setProcessingId(null); }
  };

  // Ausblenden: setzt data.aktion='manuell_ausgeblendet'. Aktion bleibt in
  // pendingActions sichtbar, aber ausgegraut + nur Wieder-aktivieren-Button.
  // Sidebar-Counter zählt ausgeblendete NICHT.
  const handleAusblenden = async (action) => {
    if (!window.confirm('Diese Aktion ausblenden? Sie wird ausgegraut und verschwindet aus dem Aktionen-Tab. Du kannst sie jederzeit wieder aktivieren.')) return;
    setProcessingId(action.id);
    try {
      await apiClient.post(`/notifications/${action.id}/ausblenden`);
      toast.success('Aktion ausgeblendet.');
      reload();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.error || e.message));
    } finally { setProcessingId(null); }
  };

  const handleAktivieren = async (action) => {
    setProcessingId(action.id);
    try {
      await apiClient.post(`/notifications/${action.id}/aktivieren`);
      toast.success('Aktion wieder aktiviert.');
      reload();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.error || e.message));
    } finally { setProcessingId(null); }
  };

  const handleSubmitCounter = async ({ datum, uhrzeit }) => {
    if (!counterPropAction) return;
    setCounterPropLoading(true);
    try {
      await apiClient.post(`/notifications/${counterPropAction.id}/counter-propose`, { datum, uhrzeit });
      toast.success('Gegenvorschlag gesendet. Der Kunde wird kontaktiert.');
      setCounterPropAction(null);
      reload();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.error || e.message));
    } finally { setCounterPropLoading(false); }
  };

  // Status-Badge-Config für erledigte Aktionen
  const STATUS_LABELS = {
    BESTAETIGT: { text: 'Bestätigt', icon: CheckCircle, className: 'bg-green-50 text-green-700 border-green-200' },
    ABGELEHNT: { text: 'Abgelehnt', icon: X, className: 'bg-red-50 text-red-700 border-red-200' },
    GEGENVORSCHLAG_GESENDET: { text: 'Gegenvorschlag gesendet', icon: Calendar, className: 'bg-blue-50 text-blue-700 border-blue-200' }
  };

  const formatTerminLabel = (d) => {
    if (!d?.datum) return '';
    return `${d.datum}${d.uhrzeit ? ' um ' + d.uhrzeit : ''}`;
  };

  const formatResolvedAt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div
      ref={containerRef}
      id="termin-aktionen-block"
      className="py-5 px-4 rounded-lg transition-shadow"
      style={{
        // Vertikaler Farbverlauf: an den Rändern transparent, in der Mitte
        // Akzentton — blendet weich in Summary oben und Transkript unten.
        background: 'linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--primary) 7%, transparent) 18%, color-mix(in srgb, var(--primary) 7%, transparent) 82%, transparent 100%)'
      }}
    >
      {visiblePending.length > 0 && (
        <p className="text-xs font-medium text-text-muted uppercase mb-3">Offene Aktionen</p>
      )}
      <div className="space-y-3">
        {visiblePending.map(action => {
          const { label, confirm, showAlternative, showEdit } = getTypeLabel(action.type);
          const d = action.data || {};
          const terminLabel = d.datum
            ? `${d.datum}${d.uhrzeit ? ' um ' + d.uhrzeit : ''}`
            : '';
          const beschreibung = d.beschreibung || action.message || '';
          const isProc = processingId === action.id;
          // Bei Terminänderungen zeigen wir explizit „Bisher → Neuer Wunsch"
          // mit dem alten Termin aus data.alterTermin (wird vom HeyKiki-
          // requestAppointmentChange-Tool mitgeschickt, toolExecuteService.js:1508).
          const istAenderung = action.type === 'TERMIN_AENDERUNG_ANFRAGE' && d.alterTermin;
          const formatTerminDatum = (raw) => {
            if (!raw) return '';
            try {
              const date = new Date(raw);
              if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
              }
            } catch { /* fall through */ }
            return String(raw);
          };
          const isAusgeblendet = !!action.ausgeblendet;
          return (
            <div
              key={action.id}
              className={`bg-white rounded-lg p-3 ${isAusgeblendet ? 'opacity-60' : 'animate-pulse-primary-glow'}`}
              style={{ border: `1px solid ${isAusgeblendet ? '#d1d5db' : 'color-mix(in srgb, var(--primary) 35%, transparent)'}` }}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{label}{!istAenderung && terminLabel ? `: ${terminLabel}` : ''}</p>
                  {istAenderung ? (
                    <div className="text-xs mt-1 space-y-0.5">
                      <p>
                        <span className="text-text-muted">Bisher: </span>
                        <span className="font-medium">
                          {formatTerminDatum(d.alterTermin.datum)}
                          {d.alterTermin.uhrzeit ? ` um ${d.alterTermin.uhrzeit} Uhr` : ''}
                        </span>
                        {d.alterTermin.titel && <span className="text-text-muted"> · {d.alterTermin.titel}</span>}
                      </p>
                      <p>
                        <span className="text-text-muted">Neuer Wunsch: </span>
                        <span className="font-medium">
                          {d.datum || '—'}
                          {d.uhrzeit ? ` um ${d.uhrzeit} Uhr` : ''}
                        </span>
                      </p>
                      {d.grund && <p className="text-text-muted italic">Grund: {d.grund}</p>}
                    </div>
                  ) : (
                    beschreibung && <p className="text-xs text-text-muted mt-0.5 line-clamp-2">{beschreibung}</p>
                  )}
                  {/* Einsatzort — Pflicht-Info für Vor-Ort-Service: Adresse muss
                      direkt in der Bestätigungs-Karte sichtbar sein, damit beim
                      Bestätigen nichts fehlt. Backend liefert d.adresse für
                      neue TERMIN_BESTAETIGUNG/AENDERUNG-Notifications; für
                      Bestands-Aktionen (vor dem Rollout erstellt) fallen wir
                      auf die Anfrage/Kunde-Relation zurück. Hover öffnet eine
                      Google-Maps-Vorschau (analog zu Kundendetail). */}
                  {(action.type === 'TERMIN_BESTAETIGUNG_ANFRAGE' || action.type === 'TERMIN_AENDERUNG_ANFRAGE') && (() => {
                    const einsatzort = d.adresse
                      || anfrage?.kunde?.adresse
                      || (anfrage?.extrahierteDaten && typeof anfrage.extrahierteDaten === 'object' ? anfrage.extrahierteDaten.adresse : null)
                      || null;
                    if (!einsatzort) return null;
                    return (
                      <p className="text-xs text-text-muted mt-1 flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(einsatzort)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-words hover:text-primary"
                          onMouseEnter={(e) => handleAddressMouseEnter(e, einsatzort)}
                          onMouseLeave={handleAddressMouseLeave}
                        >
                          {einsatzort}
                        </a>
                      </p>
                    );
                  })()}
                </div>
                {!isCompact && (
                  isAusgeblendet ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 bg-gray-200 text-gray-600">
                      <EyeOff className="w-3 h-3" /> Ausgeblendet
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
                      style={{ backgroundColor: 'color-mix(in srgb, var(--primary) 15%, transparent)', color: 'var(--primary-dark)' }}
                    >
                      <Clock className="w-3 h-3" /> Wartet auf Bestätigung
                    </span>
                  )
                )}
              </div>
              {/* Aufklappfeld „Dauer & Zuweisung anpassen" — nur bei Aktionen mit
                  konkretem Termin-Datum (also Termin-Bestätigung und Termin-
                  Änderung). KVA-Bestätigung und Stornierung legen keinen neuen
                  Termin an, deshalb dort ausgeblendet. */}
              {(action.type === 'TERMIN_BESTAETIGUNG_ANFRAGE' || action.type === 'TERMIN_AENDERUNG_ANFRAGE') && d.datum && (() => {
                const opts = optionsByAction[action.id] || {};
                const isOpen = expandedOptionsId === action.id;
                const dauer = opts.dauerMinuten ?? '';
                const kat = opts.kategorieId ? kategorienList.find(k => k.id === opts.kategorieId) : null;
                return (
                  <div className="mt-3 border-t border-gray-100 pt-2">
                    <button
                      type="button"
                      onClick={() => setExpandedOptionsId(isOpen ? null : action.id)}
                      className="flex items-center gap-1 text-xs text-text-muted hover:text-text transition"
                    >
                      {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {kategorienList.length > 0 ? 'Kategorie, Dauer & Zuweisung' : 'Dauer & Zuweisung anpassen'}
                      {!isOpen && (kat || dauer || opts.bearbeiterId) && (
                        <span className="ml-1 text-[10px] text-primary">
                          (
                          {[
                            kat?.name,
                            dauer ? `${dauer} Min` : null,
                            opts.bearbeiterId ? (benutzerList.find(b => b.id === opts.bearbeiterId)?.name || 'Mitarbeiter') : null
                          ].filter(Boolean).join(', ')}
                          )
                        </span>
                      )}
                    </button>
                    {isOpen && (
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {kategorienList.length > 0 && (
                          <div className="sm:col-span-2">
                            <label className="block text-[11px] font-medium text-text-muted mb-1">Kategorie</label>
                            <select
                              value={opts.kategorieId || ''}
                              onChange={e => applyKategorie(action.id, e.target.value || null)}
                              className="w-full px-2 py-1 text-xs rounded-md border border-gray-200 focus:border-primary focus:outline-none bg-white"
                            >
                              <option value="">— keine Kategorie —</option>
                              {kategorienList.map(k => (
                                <option key={k.id} value={k.id}>
                                  {k.name} ({k.defaultDauerMinuten} Min{k.defaultBearbeiter ? `, ${k.defaultBearbeiter.name}` : ''})
                                </option>
                              ))}
                            </select>
                            {kat && (
                              <p className="text-[10px] text-text-muted mt-1">Defaults für „{kat.name}" wurden übernommen — du kannst sie unten überschreiben.</p>
                            )}
                          </div>
                        )}
                        <div>
                          <label className="block text-[11px] font-medium text-text-muted mb-1">Dauer</label>
                          <div className="flex flex-wrap items-center gap-1">
                            {[30, 60, 90, 120].map(min => (
                              <button
                                key={min}
                                type="button"
                                onClick={() => setOptionFor(action.id, { dauerMinuten: min })}
                                className={`px-2 py-1 text-xs rounded-md border transition ${Number(dauer) === min ? 'bg-primary text-white border-primary' : 'bg-white text-text border-gray-200 hover:border-primary'}`}
                              >
                                {min} Min
                              </button>
                            ))}
                            <input
                              type="number"
                              min={5}
                              max={1440}
                              step={5}
                              placeholder="frei"
                              value={dauer}
                              onChange={e => {
                                const v = e.target.value;
                                setOptionFor(action.id, { dauerMinuten: v === '' ? null : Number(v) });
                              }}
                              className="w-16 px-2 py-1 text-xs rounded-md border border-gray-200 focus:border-primary focus:outline-none"
                            />
                            <span className="text-[11px] text-text-muted">Min</span>
                            {dauer && (
                              <button
                                type="button"
                                onClick={() => setOptionFor(action.id, { dauerMinuten: null })}
                                className="ml-1 text-[11px] text-text-muted hover:text-text underline"
                              >
                                zurücksetzen
                              </button>
                            )}
                          </div>
                          {!dauer && <p className="text-[10px] text-text-muted mt-1">Standard: 60 Min</p>}
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-text-muted mb-1">Mitarbeiter</label>
                          <select
                            value={opts.bearbeiterId || ''}
                            onChange={e => setOptionFor(action.id, { bearbeiterId: e.target.value || null })}
                            className="w-full px-2 py-1 text-xs rounded-md border border-gray-200 focus:border-primary focus:outline-none bg-white"
                          >
                            <option value="">— nicht zugewiesen —</option>
                            {benutzerList.map(b => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="flex flex-wrap gap-2 mt-2">
                {isAusgeblendet ? (
                  <button
                    onClick={() => handleAktivieren(action)}
                    disabled={isProc}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Wieder aktivieren
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleConfirm(action)}
                      disabled={isProc}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      {confirm}
                    </button>
                    {showAlternative && (
                      <button
                        onClick={() => setCounterPropAction(action)}
                        disabled={isProc}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        Alternative vorschlagen
                      </button>
                    )}
                    {showEdit && (
                      <button
                        onClick={() => handleEditKva(action)}
                        disabled={isProc}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Bearbeiten
                      </button>
                    )}
                    <button
                      onClick={() => handleReject(action)}
                      disabled={isProc}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-500 text-white rounded-md hover:bg-gray-600 transition disabled:opacity-50"
                    >
                      <X className="w-3.5 h-3.5" />
                      Ablehnen
                    </button>
                    <button
                      onClick={() => handleAusblenden(action)}
                      disabled={isProc}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-text-muted border border-gray-300 rounded-md hover:bg-gray-50 hover:text-text transition disabled:opacity-50"
                      title="Manuell als erledigt markieren — Aktion wird ausgegraut"
                    >
                      <EyeOff className="w-3.5 h-3.5" />
                      Ausblenden
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Durchgeführte Aktionen */}
      {completed.length > 0 && (
        <>
          <p className={`text-xs font-medium text-text-muted uppercase ${visiblePending.length > 0 ? 'mt-4' : ''} mb-3`}>Durchgeführte Aktionen</p>
          <div className="space-y-2">
            {completed.map(action => {
              const { label } = getTypeLabel(action.type);
              const statusCfg = STATUS_LABELS[action.status] || STATUS_LABELS.BESTAETIGT;
              const StatusIcon = statusCfg.icon;
              const terminLabel = formatTerminLabel(action.data);
              // Für Gegenvorschläge zusätzlich das neu vorgeschlagene Datum zeigen.
              // Counter-propose-Handler speichert es unter data.gegenvorschlag={datum,uhrzeit}.
              const gv = action.data?.gegenvorschlag;
              const gvLabel = gv?.datum ? `${gv.datum}${gv.uhrzeit ? ' um ' + gv.uhrzeit : ''}` : null;
              // Bei KVA-Bestätigungen: KVA-Nummer im Titel + Klick-Ziel nach Status.
              // data.kvaNummer/kvaId werden von notificationService.js:1099 gesetzt.
              // `anfrage.kostenvoranschlaege` ist in anfrageController.js:129 mitgeladen —
              // von dort holen wir live-Status + pdfPfad (vs. dem im Notification-Data-Blob,
              // der zum Zeitpunkt der Notification eingefroren ist).
              const isKva = action.type === 'KVA_BESTAETIGUNG_ANFRAGE';
              const kvaNummer = action.data?.kvaNummer;
              const kvaId = action.data?.kvaId;
              const kvaSummary = isKva ? (kvaNummer ? `KVA Nr. ${kvaNummer}` : 'KVA') : null;
              const kvaRecord = isKva && kvaId
                ? (anfrage.kostenvoranschlaege || []).find(k => k.id === kvaId)
                : null;
              const kvaIsEntwurf = kvaRecord?.status === 'entwurf';
              const kvaPdfUrl = kvaRecord && !kvaIsEntwurf ? kvaApi.getPdfUrl(kvaRecord) : null;
              // Klick-Ziel: Entwurf → Edit-Seite; versendet/angenommen/... mit PDF → PDF;
              // Fallback (kein Record gefunden) → Edit-Seite (führt ggf. zur 404).
              const kvaOpen = isKva && kvaId
                ? () => {
                    if (kvaPdfUrl) {
                      window.open(kvaPdfUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(`/kostenvoranschlaege/${kvaId}/bearbeiten`);
                    }
                  }
                : null;
              const kvaLinkLabel = kvaPdfUrl ? 'PDF öffnen →' : (kvaIsEntwurf ? 'KVA bearbeiten →' : 'KVA öffnen →');
              return (
                <div
                  key={action.id}
                  className={`bg-white rounded-lg border border-border p-3 ${kvaOpen ? 'cursor-pointer hover:border-primary/40 hover:bg-gray-50 transition' : ''}`}
                  onClick={kvaOpen || undefined}
                  role={kvaOpen ? 'button' : undefined}
                  tabIndex={kvaOpen ? 0 : undefined}
                  onKeyDown={kvaOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); kvaOpen(); } } : undefined}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{label}{terminLabel ? `: ${terminLabel}` : ''}{kvaSummary ? `: ${kvaSummary}` : ''}</p>
                      {isKva && action.data?.kundenName && (
                        <p className="text-xs text-text-muted mt-0.5">
                          Kunde: <span className="font-medium">{action.data.kundenName}</span>
                        </p>
                      )}
                      {action.data?.beschreibung && (
                        <p className="text-xs text-text-muted mt-0.5 line-clamp-2">{action.data.beschreibung}</p>
                      )}
                      {gvLabel && (
                        <p className="text-xs mt-1 flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-text-muted" />
                          <span className="text-text-muted">Neuer Vorschlag:</span>
                          <span className="font-medium">{gvLabel}</span>
                        </p>
                      )}
                      <p className="text-[11px] text-text-muted mt-1">
                        {formatResolvedAt(action.resolvedAt)}
                      </p>
                      {kvaOpen && (
                        <p className="text-[11px] text-primary mt-1 font-medium">{kvaLinkLabel}</p>
                      )}
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 border ${statusCfg.className}`}>
                      <StatusIcon className="w-3 h-3" /> {statusCfg.text}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <CounterProposalModal
        isOpen={!!counterPropAction}
        onClose={() => setCounterPropAction(null)}
        onSubmit={handleSubmitCounter}
        isLoading={counterPropLoading}
        originalTermin={counterPropAction?.data?.datum ? `${counterPropAction.data.datum}${counterPropAction.data.uhrzeit ? ' um ' + counterPropAction.data.uhrzeit : ''}` : null}
      />

      {/* Google-Maps-Hover-Preview für Einsatzort. Fixed-Positioning damit es
          aus der Action-Card herausschwebt, ohne den Layout-Flow der Karte
          zu sprengen. */}
      {mapPreview.open && mapPreview.address && (
        <div
          className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fade-in"
          style={{
            top: mapPreview.top,
            left: mapPreview.left,
            width: '320px',
            maxWidth: 'calc(100vw - 16px)'
          }}
          onMouseEnter={handleMapMouseEnter}
          onMouseLeave={handleMapMouseLeave}
        >
          <div className="relative">
            <iframe
              title="Google Maps Preview"
              width="100%"
              height="200"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(mapPreview.address)}&output=embed&z=15`}
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/10 to-transparent" />
          </div>
          <div className="p-3 bg-white">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{mapPreview.address}</p>
                <p className="text-xs text-gray-500 mt-0.5">Klicken für Vollbild in Google Maps</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Manuell View ────────────────────────────────────────────────────────────────

function ManuellView({ anfrage, onSkizze }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border bg-gray-50/50 flex items-center gap-2">
        <FileText className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-800">Anfrage Details</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Nachricht */}
        <div>
          <p className="text-xs font-medium text-text-muted uppercase mb-1">Nachricht</p>
          <div className="bg-white rounded-lg border border-border p-4">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{anfrage.nachricht || '–'}</p>
          </div>
        </div>

        {/* Extrahierte Daten */}
        {anfrage.extrahierteDaten && Object.keys(anfrage.extrahierteDaten).length > 0 && (
          <div>
            <p className="text-xs font-medium text-text-muted uppercase mb-1">Extrahierte Daten</p>
            <div className="bg-white rounded-lg border border-border p-4 space-y-2">
              {Object.entries(anfrage.extrahierteDaten)
                .filter(([k]) => !['_', 'messageId', 'threadId'].some(x => k.startsWith(x)))
                .filter(([k]) => !(isHeykikiOrg && HEYKIKI_HIDDEN_EXTRACTED_FIELDS.has(k)))
                .map(([key, value]) => (
                  <div key={key} className="flex items-start gap-2">
                    <span className="text-xs font-medium text-text-muted w-32 shrink-0">{formatFieldLabel(key)}:</span>
                    <span className="text-sm text-text">
                      {formatFieldValue(key, value)}
                    </span>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="pt-2 border-t border-border text-xs text-text-muted space-y-1">
          <p>Erstellt: {formatDateTime(anfrage.erstelltAm)}{anfrage.erstelltVon ? ` von ${anfrage.erstelltVon}` : ''}</p>
          {anfrage.aktualisiertAm && anfrage.aktualisiertAm !== anfrage.erstelltAm && (
            <p>Aktualisiert: {formatDateTime(anfrage.aktualisiertAm)}</p>
          )}
        </div>
      </div>

      {/* Fotos & Dokumente (Kundendetail-Stil) */}
      <AnhangSection anfrage={anfrage} onSkizze={onSkizze} />
    </div>
  );
}

// ── Right Sidebar: Detail Panel ─────────────────────────────────────────────────

// Card-Wrapper für rechte-Spalten-Sektionen — Lucas/Husmann&Dreier-Feedback
// 2026-05-07: Sections sollen klar voneinander getrennt sein (analog dem
// alten HeyKiki-Layout), nicht ein durchgehender Stream. Hellgrauer
// Hintergrund + Border setzt sich vom äußeren bg-surface ab.
function SectionCard({ title, children, className = '' }) {
  return (
    <div className={`bg-gray-50/70 rounded-lg p-4 border border-gray-100 ${className}`}>
      {title && (
        <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">{title}</p>
      )}
      {children}
    </div>
  );
}

const DETAIL_TABS = [
  { value: 'aktionen', label: 'Aktionen' },
  { value: 'details',  label: 'Details'  },
  { value: 'verlauf',  label: 'Verlauf'  },
];

function DetailPanel({ anfrage, onEdit, onStatusChange, onDelete, statusUpdating, onZusammenfassung, summarizing, onSkizze, onTerminErstellen, onTerminBestaetigen, benutzerList = [], onAssignBearbeiter, assigningBearbeiter = false, onCollapse, onRefresh }) {
  if (!anfrage) return null;

  const navigate = useNavigate();
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const sc = STATUS_CONFIG[anfrage.status] || STATUS_CONFIG.neu;
  // Tab-State für rechte Spalte: Aktionen | Details | Verlauf —
  // Lucas/Husmann&Dreier-Feedback 2026-05-07. Persistiert per anfrageId,
  // damit beim Wechsel der ausgewählten Anfrage das Tab erhalten bleibt.
  const [activeTab, setActiveTab] = useState('aktionen');
  // Container-Width-Tracking für „Wartet auf Bestätigung"-Pills im
  // Termin-Block (analog TerminActionsBlock).
  const detailContainerRef = useRef(null);
  const [isDetailCompact, setIsDetailCompact] = useState(false);
  useEffect(() => {
    if (!detailContainerRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsDetailCompact(entry.contentRect.width < 380);
      }
    });
    observer.observe(detailContainerRef.current);
    return () => observer.disconnect();
  }, []);
  const katConf = KATEGORIE_CONFIG[anfrage.kategorie];
  const agentConf = AGENT_STATUS_CONFIG[anfrage.agentStatus];
  const channel = detectChannel(anfrage);

  // "Termin bestätigen" nur zeigen, wenn zur Anfrage ein Pending-Termin vorliegt.
  // Quellen: pendingActions mit TERMIN-Typ (Anfrage-/Änderungs-Bestätigung) ODER
  // terminBestaetigung[status=PENDING]. Das spiegelt die Logik von TerminActionsBlock —
  // der Button ist der visuelle Shortcut auf die dort angebotene Aktion.
  const pendingTerminActions = [
    ...(anfrage.pendingActions || []).filter(a =>
      a.type === 'TERMIN_BESTAETIGUNG_ANFRAGE' ||
      a.type === 'TERMIN_AENDERUNG_ANFRAGE' ||
      a.type === 'TERMIN_STORNIERUNG_ANFRAGE'
    ),
    ...(anfrage.terminBestaetigung || []).filter(tb => tb.status === 'PENDING')
  ];
  const hasPendingTermin = pendingTerminActions.length > 0;

  return (
    <div ref={detailContainerRef} className="flex flex-col h-full">
      {/* Header — Lucas/Husmann&Dreier-Feedback 2026-05-07: Schrift +Buttons
          deutlich größer + mehr Innen-Padding für ruhigeres Erscheinen. */}
      <div className={`p-5 border-b border-border ${anfrage.istNotfall ? 'bg-red-50/60 border-red-200' : ''}`}>
        <div className="flex items-start gap-2 mb-2">
          {anfrage.istNotfall && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-red-600 text-white rounded shrink-0 mt-0.5"
              title="Notdienst — Anruf wurde an den Notdienst weitergeleitet"
            >
              <AlertTriangle className="w-3 h-3" />
              Notdienst
            </span>
          )}
          <h3 className={`text-base font-semibold truncate flex-1 min-w-0 ${anfrage.istNotfall ? 'text-red-900' : 'text-text'}`}>
            {(anfrage.betreff || '').replace(/^🚨\s*NOTFALL\s*—\s*/i, '').trim() || '—'}
          </h3>
          {onCollapse && (
            <button
              onClick={onCollapse}
              className="shrink-0 p-1.5 hover:bg-gray-100 rounded-md transition -mt-0.5 -mr-1"
              title="Detail-Panel einklappen"
            >
              <ChevronRight className="w-5 h-5 text-text-muted" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${sc.color}`}>{sc.label}</span>
          {anfrage.kategorie && katConf && (
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${katConf.color}`}>
              {anfrage.kategorie}
            </span>
          )}
          {agentConf && anfrage.agentStatus && anfrage.agentStatus !== 'NEU' && (
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${agentConf.color}`}>
              {agentConf.label}
            </span>
          )}
        </div>
      </div>

      {/* Tab-Bar — Lucas/Husmann&Dreier-Feedback 2026-05-07: Aktionen,
          Details und Verlauf werden getrennt, damit jeder Tab kompakt
          ohne Scrollen ins Viewport passt. */}
      <div className="px-4 pt-3 border-b border-border bg-surface">
        <div className="flex gap-1">
          {DETAIL_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setActiveTab(t.value)}
              className={`px-3.5 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === t.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content — pro Tab eigene Sektionen, alle als Cards eingewickelt
          damit die Optik nicht „abgehakt" wirkt. */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4">

      {activeTab === 'aktionen' && (
        <>

        {/* Zugewiesen an — Lucas-Feedback 2026-05-07: nach ganz oben,
            damit Disponent als allererstes sieht/setzt wer zuständig ist. */}
        {onAssignBearbeiter && (
          <SectionCard title="Zugewiesen an">
            <div className="relative">
              <select
                value={anfrage.bearbeiterId || ''}
                onChange={(e) => onAssignBearbeiter(e.target.value)}
                disabled={assigningBearbeiter}
                className="w-full px-3 py-2 text-sm bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
              >
                <option value="">— Nicht zugewiesen —</option>
                {benutzerList.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {anfrage.bearbeiter && (
                <p className="text-sm text-text-muted mt-1.5">
                  Aktuell: <span className="font-medium text-text">{anfrage.bearbeiter.name}</span>
                  {anfrage.bearbeiter.loginAktiv === false && <span className="ml-1 text-gray-400">(ohne Login)</span>}
                </p>
              )}
            </div>
          </SectionCard>
        )}

        {/* Offene Aktionen — Bearbeiter-Fokus auf ausstehende Entscheidungen.
            Durchgeführte Aktionen wandern in den Verlauf-Tab (mode='pending'). */}
        <TerminActionsBlock anfrage={anfrage} onActionComplete={onRefresh} mode="pending" />

        {/* Quick Actions */}
        <SectionCard title="Status-Aktionen">
          <div className="space-y-2">
            {anfrage.status !== 'abgeschlossen' ? (
              <button
                onClick={() => onStatusChange('abgeschlossen')}
                disabled={statusUpdating}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Als erledigt markieren
              </button>
            ) : (
              <button
                onClick={() => onStatusChange('neu')}
                disabled={statusUpdating}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition disabled:opacity-50"
              >
                <AlertCircle className="w-4 h-4" />
                Wieder öffnen
              </button>
            )}
            {anfrage.status !== 'in_bearbeitung' && (
              <button
                onClick={() => onStatusChange('in_bearbeitung')}
                disabled={statusUpdating}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-lg transition disabled:opacity-50"
              >
                <Clock className="w-4 h-4" />
                In Bearbeitung setzen
              </button>
            )}
            <button
              onClick={onEdit}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition"
            >
              <Pencil className="w-4 h-4" />
              Bearbeiten
            </button>
            {anfrage.kunde && (
              <Link
                to={`/kostenvoranschlaege/neu?kundeId=${anfrage.kundeId}&anfrageId=${anfrage.id}`}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                <ClipboardList className="w-4 h-4" />
                KVA erstellen
              </Link>
            )}
            {anfrage.nachricht && anfrage.nachricht.length > 500 && (
              <button
                onClick={onZusammenfassung}
                disabled={summarizing}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg transition disabled:opacity-50"
              >
                {summarizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {summarizing ? 'Fasst zusammen...' : 'KI Zusammenfassung'}
              </button>
            )}
            {!isHeykikiOrg && (
              <button
                onClick={onSkizze}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                <PenTool className="w-4 h-4" />
                Skizze erstellen
              </button>
            )}
            {isHeykikiOrg && (
              <button
                onClick={onTerminErstellen}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                <Calendar className="w-4 h-4" />
                Termin erstellen
              </button>
            )}
            {/* „Termin bestätigen"-Button wurde entfernt — die Aktion gehört
                in den Block „Offene Aktionen" oben, nicht in die Status-Spalte. */}
          </div>
        </SectionCard>

        {/* Dokumente — alle mit der Anfrage verknüpften PDFs (Arbeitsnachweis,
            Kostenvoranschlag, Rechnung, Uploads) als Quick-Links direkt im
            Aktionen-Tab. Sortiert nach Datum absteigend, mit Kategorie-Badge
            damit man auf einen Blick sieht was es ist. */}
        {(() => {
          const dokumente = (anfrage.dokumente || [])
            .slice()
            .sort((a, b) => new Date(b.erstelltAm) - new Date(a.erstelltAm));
          if (dokumente.length === 0) return null;
          const badgeFor = (kat) => {
            if (kat === 'Arbeitsnachweis')  return 'bg-emerald-50 text-emerald-700';
            if (kat === 'Kostenvoranschlag') return 'bg-blue-50 text-blue-700';
            if (kat === 'Rechnung')          return 'bg-purple-50 text-purple-700';
            return 'bg-gray-50 text-gray-700';
          };
          return (
            <SectionCard title={`Dokumente (${dokumente.length})`}>
              <div className="space-y-1.5">
                {dokumente.map(d => {
                  const label = d.originalname || d.dateiname || 'Dokument';
                  const kat = d.kategorie || 'Dokument';
                  return (
                    <button
                      key={d.id}
                      onClick={() => openDokumentInBrowser(d.id, label)}
                      type="button"
                      className="block w-full text-left p-3 bg-white rounded-lg text-base hover:bg-gray-50 transition border border-gray-100"
                      title="PDF öffnen"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">{label}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${badgeFor(kat)}`}>
                          {kat}
                        </span>
                      </div>
                      <p className="text-sm text-text-muted mt-0.5">{formatDate(d.erstelltAm)}</p>
                    </button>
                  );
                })}
              </div>
            </SectionCard>
          );
        })()}

        </>
      )}

      {activeTab === 'details' && (
        <>

        {/* Zusammenfassungen aller Calls (Lucas-Feedback 2026-05-19):
            Bei verknüpften Outbound-Calls (Terminbestätigung etc.) gibt's
            jetzt mehrere CallLogs an einer Anfrage. Vorher hat `.find(c => c.summary)`
            nur die ERSTE Summary genommen — und da `callLogs` desc sortiert
            ist, war das immer die Outbound-Summary, der ursprüngliche Inbound-
            Kontext verschwand. Jetzt: alle Summaries chronologisch (älteste
            zuerst), mit Header pro Eintrag, damit Inbound + Outbound nebeneinander
            stehen. */}
        {(() => {
          const summaries = (anfrage.callLogs || [])
            .filter(c => c.summary)
            .slice()
            .sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt));
          if (summaries.length === 0) return null;
          return (
            <SectionCard title={summaries.length === 1 ? 'Zusammenfassung' : `Zusammenfassungen (${summaries.length})`}>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {summaries.map(call => {
                  const isOutbound = call.direction === 'outbound';
                  const anlassLabel = isOutbound
                    ? (OUTBOUND_ANLASS_LABELS[call.outboundCall?.anlassTyp] || 'Ausgehender Anruf')
                    : 'Eingehender Anruf';
                  return (
                    <div key={call.id} className={summaries.length > 1 ? 'pb-3 border-b border-gray-100 last:border-b-0 last:pb-0' : ''}>
                      {summaries.length > 1 && (
                        <div className="flex items-center gap-1.5 mb-1.5 text-xs text-text-muted">
                          <Phone className={`w-3 h-3 ${isOutbound ? 'text-primary' : 'text-purple-600'}`} />
                          <span className="font-medium">{anlassLabel}</span>
                          <span>·</span>
                          <span>{formatDateTime(call.startedAt)}</span>
                        </div>
                      )}
                      <p className="text-sm leading-relaxed text-text whitespace-pre-wrap">{call.summary}</p>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          );
        })()}

        {/* Kanal */}
        <SectionCard title="Kanal">
          <div className="flex items-center gap-2 text-base">
            {channel === 'whatsapp' && <><Smartphone className="w-5 h-5 text-green-600" /><span>WhatsApp</span></>}
            {channel === 'email' && <><Mail className="w-5 h-5 text-blue-600" /><span>E-Mail</span></>}
            {channel === 'telefon' && <><Phone className="w-5 h-5 text-purple-600" /><span>Telefon</span></>}
            {channel === 'manuell' && <><FileText className="w-5 h-5 text-gray-600" /><span>Manuell</span></>}
          </div>
        </SectionCard>

        {/* Kunde — Schrift hochgezogen für Handwerker-Lesbarkeit
            (Lucas-Feedback 2026-05-07). */}
        {anfrage.kunde && (
          <SectionCard title="Kunde">
            <Link
              to={`/kunden/${anfrage.kunde.id}`}
              className="block p-3.5 bg-white rounded-lg hover:bg-gray-50 transition border border-gray-100"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <UserCircle className="w-5 h-5 text-text-muted" />
                <span className="text-base font-medium text-text">{anfrage.kunde.name}</span>
                <ExternalLink className="w-3.5 h-3.5 text-text-muted ml-auto" />
              </div>
              {anfrage.kunde.kundennummer && (
                <p className="text-sm text-text-muted ml-7 font-mono">#{anfrage.kunde.kundennummer}</p>
              )}
              {anfrage.kunde.email && (
                <p className="text-sm text-text-muted ml-7">{anfrage.kunde.email}</p>
              )}
              {anfrage.kunde.telefon && (
                <p className="text-sm text-text-muted ml-7">{anfrage.kunde.telefon}</p>
              )}
              {anfrage.kunde.adresse && (
                <p className="text-sm text-text-muted ml-7 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />{anfrage.kunde.adresse}
                </p>
              )}
            </Link>
          </SectionCard>
        )}

        {/* Objekt */}
        {anfrage.objekt && (
          <SectionCard title="Objekt">
            <div className="p-3.5 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-600" />
                <span className="text-base font-medium text-purple-900">{anfrage.objekt.name || anfrage.objekt.strasse}</span>
              </div>
              {anfrage.objekt.strasse && anfrage.objekt.name && (
                <p className="text-sm text-purple-600 ml-7">{anfrage.objekt.strasse}{anfrage.objekt.ort ? `, ${anfrage.objekt.ort}` : ''}</p>
              )}
            </div>
          </SectionCard>
        )}

        {/* Nachricht-Block entfernt (2026-04-22): Der vom KI-Telefonassistenten
            auto-generierte „Telefonische Terminanfrage …"-Text ist redundant
            mit Zusammenfassung + Transkript + Offenen Aktionen und wurde als
            Rausch empfunden. Echte manuelle Nachrichten werden weiterhin unter
            „Zusammenfassung" oder im Transkript sichtbar. */}

        {/* Extrahierte Details - DYNAMISCH für alle Pflichtfelder (Multi-Tenancy!).
            Bei HeyKiki-Sub-Orgs bewusst ausgeblendet: die Daten wirken in diesem
            Kontext verwirrend (Provenance-Felder, interne Keys) und das Wesentliche
            steht bereits im Transkript/in der Zusammenfassung. */}
        {!isHeykikiOrg && anfrage.extrahierteDaten && Object.keys(anfrage.extrahierteDaten).length > 0 && (() => {
          const entries = Object.entries(anfrage.extrahierteDaten)
            // pflichtfelder-Objekt flach auflösen
            .flatMap(([key, value]) => {
              if (key === 'pflichtfelder' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
                return Object.entries(value);
              }
              return [[key, value]];
            })
            .filter(([key, value]) => {
              // Technische Felder ausschließen
              const excludeKeys = ['gewaehlterTermin', 'wunschtermin', 'preisanfrage', 'dringend', 'vorgeschlageneTermine', 'aufmasse', 'dokumentAnfrage', 'arbeitsnachweise', 'letzterArbeitsnachweis', 'gesamtStunden', 'messageId', 'threadId'];
              if (excludeKeys.includes(key)) return false;
              if (key.startsWith('_')) return false;
              if (isHeykikiOrg && HEYKIKI_HIDDEN_EXTRACTED_FIELDS.has(key)) return false;
              // Leere Werte ausschließen
              if (value === null || value === undefined || value === '' || value === false) return false;
              // Objekte nur wenn sie Inhalt haben
              if (typeof value === 'object' && Object.keys(value).length === 0) return false;
              return true;
            });
          if (entries.length === 0) return null;
          const formatLabel = (k) => k
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .split(' ')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(' ')
            .trim();
          const formatValue = (v) => {
            if (typeof v === 'object') return JSON.stringify(v);
            if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
            return String(v);
          };
          return (
            <SectionCard title="Extrahierte Details">
              <div className="p-3 bg-primary-light rounded-lg space-y-1.5">
                {entries.map(([key, value]) => (
                  <div key={key} className="text-xs">
                    <span className="text-text-muted">{formatLabel(key)}: </span>
                    <span className="font-medium text-text break-words">{formatValue(value)}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          );
        })()}

        </>
      )}

      {activeTab === 'verlauf' && (
        <>

        {/* Durchgeführte Aktionen — vorher in der Aktionen-Sektion direkt
            unter den offenen, jetzt in den Verlauf-Tab verschoben (Lucas-
            Feedback 2026-05-07: das ist Historie, nicht Aktion). */}
        <TerminActionsBlock anfrage={anfrage} onActionComplete={onRefresh} mode="completed" />

        {/* Termine (bereits angelegt = bestätigt) + Pending-Bestätigungen */}
        {(anfrage.termine?.length > 0 || anfrage.terminBestaetigung?.length > 0) && (
          <SectionCard title={`Termine (${(anfrage.termine?.length || 0) + (anfrage.terminBestaetigung?.filter(tb => tb.status === 'PENDING').length || 0)})`}>
            <div className="space-y-1.5">
              {anfrage.termine?.map(t => {
                const byKiAgent = t.erstelltVon && /KI-|KI_|Telefonassistent|Agent|ElevenLabs|VAPI/i.test(t.erstelltVon);
                return (
                  <div key={t.id} className="p-3 bg-white rounded-lg text-base border border-gray-100">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium">{t.titel || t.art || 'Termin'}</p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Bestätigt
                        </span>
                        {byKiAgent && (
                          <span className="text-xs font-medium bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                            KI
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-text-muted mt-0.5">
                      {formatDateTime(t.datum)}{t.uhrzeit ? ` · ${t.uhrzeit}` : ''}
                      {t.dauerMinuten ? ` · ${t.dauerMinuten} Min` : ''}
                    </p>
                    {t.erstelltVon && (
                      <p className="text-xs text-text-muted mt-0.5">Angelegt von: {t.erstelltVon}</p>
                    )}
                    {t.beschreibung && <p className="text-sm text-text-muted mt-0.5">{t.beschreibung}</p>}
                  </div>
                );
              })}

              {anfrage.terminBestaetigung?.filter(tb => tb.status === 'PENDING').map(tb => (
                <div key={tb.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-base">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium">{tb.terminTyp || 'Termin-Anfrage'}</p>
                    {!isDetailCompact && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0">
                        <Clock className="w-3 h-3" /> Wartet auf Bestätigung
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-muted mt-0.5">
                    {formatDateTime(tb.datum)}{tb.uhrzeit ? ` · ${tb.uhrzeit}` : ''}
                    {tb.dauer ? ` · ${tb.dauer} Min` : ''}
                  </p>
                  {tb.beschreibung && <p className="text-sm text-text-muted mt-0.5">{tb.beschreibung}</p>}
                  {tb.kiBegruendung && (
                    <p className="text-xs text-amber-700 italic mt-1">KI-Begründung: {tb.kiBegruendung}</p>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* KVAs — Klick öffnet das PDF im neuen Tab. Fallback auf die
            Bearbeiten-Seite, wenn noch kein PDF generiert wurde (Entwurf).
            Vorher war der `<Link>` auf `/kostenvoranschlaege/:id` gemappt —
            diese Route existiert aber nicht (nur `.../neu` und
            `.../:id/bearbeiten`), und der Catch-All in App.jsx hat jeden
            Aufruf stumm auf `/dashboard` umgeleitet. */}
        {anfrage.kostenvoranschlaege?.length > 0 && (
          <SectionCard title={`Kostenvoranschläge (${anfrage.kostenvoranschlaege.length})`}>
            <div className="space-y-1.5">
              {anfrage.kostenvoranschlaege.map(kva => {
                const hasPdf = !!kva.pdfPfad;
                const label = kva.nummer || 'KVA';
                const handleClick = hasPdf
                  ? () => openUploadPdfInBrowser(kva.pdfPfad, label)
                  : () => navigate(`/kostenvoranschlaege/${kva.id}/bearbeiten`);
                return (
                  <button
                    key={kva.id}
                    onClick={handleClick}
                    type="button"
                    className="block w-full text-left p-3 bg-white rounded-lg text-base hover:bg-gray-50 transition border border-gray-100"
                    title={hasPdf ? 'PDF öffnen' : 'Noch kein PDF — zum Bearbeiten öffnen'}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{label}</span>
                      {kva.gesamtBetrag != null && (
                        <span className="text-sm font-medium">{Number(kva.gesamtBetrag).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                      )}
                    </div>
                    <p className="text-sm text-text-muted mt-0.5">{formatDate(kva.erstelltAm)}{!hasPdf && ' · noch kein PDF'}</p>
                  </button>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* Ausgehende Anrufe (H&D-Feedback 2026-05-19) — kompakte Übersicht der
            Outbound-Calls, die aus dieser Anfrage entstanden sind (z.B.
            Terminbestätigungs- oder Gegenvorschlags-Anrufe). Audio + volles
            Transkript bleiben in der mittleren Telefon-Spalte; hier nur
            Datum + Anlass + prominentes Ergebnis-Badge, damit der Disponent
            den Status auf einen Blick sieht — auch bei E-Mail-/WhatsApp-
            Anfragen, die einen Outbound-Folgeanruf hatten. */}
        {(() => {
          const outboundCalls = (anfrage.callLogs || []).filter(c => c.direction === 'outbound');
          if (outboundCalls.length === 0) return null;
          return (
            <SectionCard title={`Ausgehende Anrufe (${outboundCalls.length})`}>
              <div className="space-y-1.5">
                {outboundCalls.map(call => {
                  const anlassLabel = OUTBOUND_ANLASS_LABELS[call.outboundCall?.anlassTyp] || call.outboundCall?.anlassTyp || 'Ausgehender Anruf';
                  const badge = getOutboundBadge(call.outboundCall);
                  return (
                    <div key={call.id} className="p-3 bg-white rounded-lg text-base border border-gray-100">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5 text-primary" />
                          {anlassLabel}
                        </p>
                        {badge && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap shrink-0 ${badge.tone}`}>
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-muted mt-0.5">
                        {formatDateTime(call.startedAt)}
                        {call.durationSeconds ? ` · ${formatDuration(call.durationSeconds)}` : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          );
        })()}

        {/* Arbeitsnachweise — als Dokument-Records mit kategorie='Arbeitsnachweis'
            gespeichert; nutzen den authenticated `/dokumente/download/:id`-Endpoint
            statt eines direkten Pfads. */}
        {(() => {
          const arbeitsnachweise = (anfrage.dokumente || []).filter(d => d.kategorie === 'Arbeitsnachweis');
          if (arbeitsnachweise.length === 0) return null;
          return (
            <SectionCard title={`Arbeitsnachweise (${arbeitsnachweise.length})`}>
              <div className="space-y-1.5">
                {arbeitsnachweise.map(d => {
                  const label = d.originalname || d.dateiname || 'Arbeitsnachweis';
                  return (
                    <button
                      key={d.id}
                      onClick={() => openDokumentInBrowser(d.id, label)}
                      type="button"
                      className="block w-full text-left p-3 bg-white rounded-lg text-base hover:bg-gray-50 transition border border-gray-100"
                      title="PDF öffnen"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{label}</span>
                      </div>
                      <p className="text-sm text-text-muted mt-0.5">{formatDate(d.erstelltAm)}</p>
                    </button>
                  );
                })}
              </div>
            </SectionCard>
          );
        })()}

        {/* Zeiterfassungen */}
        {anfrage.zeiterfassungen?.length > 0 && (
          <SectionCard title={`Zeiterfassung (${anfrage.zeiterfassungen.length})`}>
            <div className="space-y-1.5">
              {anfrage.zeiterfassungen.map(ze => (
                <div key={ze.id} className="p-3 bg-white rounded-lg text-base border border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{ze.beschreibung || 'Arbeit'}</span>
                    {ze.stunden != null && <span className="text-sm font-medium">{ze.stunden}h</span>}
                  </div>
                  <p className="text-sm text-text-muted mt-0.5">{formatDate(ze.datum)}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Unterschriften */}
        {anfrage.unterschriften?.length > 0 && (
          <SectionCard title={`Unterschriften (${anfrage.unterschriften.length})`}>
            <div className="space-y-1.5">
              {anfrage.unterschriften.map(u => (
                <div key={u.id} className="p-3 bg-white rounded-lg text-base border border-gray-100">
                  <p className="font-medium">{u.typ || 'Unterschrift'}</p>
                  <p className="text-sm text-text-muted mt-0.5">{formatDate(u.erstelltAm)}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Fotos (compact in sidebar) */}
        {anfrage.fotos?.length > 0 && (
          <SectionCard title={`Fotos (${anfrage.fotos.length})`}>
            <div className="grid grid-cols-3 gap-1.5">
              {anfrage.fotos.slice(0, 6).map(foto => {
                const url = getFotoUrl(foto);
                return (
                  <div key={foto.id} className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative group/foto">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full h-full"
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </a>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSkizze(url);
                      }}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/foto:opacity-100 transition flex items-center justify-center shadow"
                      title="Skizzieren"
                    >
                      <PenTool className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              {anfrage.fotos.length > 6 && (
                <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center text-sm text-text-muted font-medium">
                  +{anfrage.fotos.length - 6}
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* Empty-State, falls Verlauf-Tab komplett leer ist */}
        {!(anfrage.completedActions?.length > 0) &&
         !(anfrage.termine?.length > 0 || anfrage.terminBestaetigung?.length > 0) &&
         !(anfrage.kostenvoranschlaege?.length > 0) &&
         !((anfrage.callLogs || []).some(c => c.direction === 'outbound')) &&
         !(anfrage.zeiterfassungen?.length > 0) &&
         !(anfrage.unterschriften?.length > 0) &&
         !(anfrage.fotos?.length > 0) && (
          <div className="text-center text-base text-text-muted py-8">
            Noch keine durchgeführten Aktionen, Termine oder Dokumente.
          </div>
        )}

        </>
      )}

      {/* Meta + Delete sind tab-übergreifend sichtbar — gehören in
          die Stammdaten, aber als Footer der rechten Spalte. */}
      {activeTab === 'details' && (
        <SectionCard title="Anfrage-Info" className="bg-transparent border-dashed">
          <div className="text-sm text-text-muted space-y-1">
            <p>Erstellt: {formatDateTime(anfrage.erstelltAm)}</p>
            {anfrage.erstelltVon && <p>Von: {anfrage.erstelltVon}</p>}
            {anfrage.aktualisiertAm && <p>Aktualisiert: {formatDateTime(anfrage.aktualisiertAm)}</p>}
          </div>
        </SectionCard>
      )}

      {activeTab === 'aktionen' && (
        /* Delete — bewusst nur im Aktionen-Tab, weil's eine destruktive
           Handlung ist und am unteren Ende der Aktions-Liste sitzt. */
        <button
          onClick={onDelete}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition mt-2"
        >
          <Trash2 className="w-4 h-4" />
          Anfrage löschen
        </button>
      )}
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────────

export default function AnfrageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user: currentUser } = useAuth();
  const isHeykikiOrg = currentUser?.organisation?.orgType === 'HEYKIKI';

  // Data state
  const [anfragen, setAnfragen] = useState([]);
  const [selectedAnfrage, setSelectedAnfrage] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Sidebar filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  // Bearbeiter-Filter: 'alle' | 'me' | 'unassigned' | '<benutzerId>'
  const [bearbeiterFilter, setBearbeiterFilter] = useState(() => {
    try { return localStorage.getItem('anfrageDetail.bearbeiterFilter') || 'alle'; } catch { return 'alle'; }
  });
  const handleBearbeiterFilterChange = (value) => {
    setBearbeiterFilter(value);
    try { localStorage.setItem('anfrageDetail.bearbeiterFilter', value); } catch {}
  };
  // Sortierung der Anfragen-Liste (persistiert pro Browser)
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('anfrageDetail.sortBy') || 'newest'; } catch { return 'newest'; }
  });
  const handleSortByChange = (value) => {
    setSortBy(value);
    try { localStorage.setItem('anfrageDetail.sortBy', value); } catch {}
  };
  // Bulk-Selection in der Sidebar (nur sichtbar bei breiter Sidebar)
  const [selectedSidebarIds, setSelectedSidebarIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSidebarSelect = (id) => {
    setSelectedSidebarIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSidebarSelectAll = (visibleIds = []) => {
    setSelectedSidebarIds(prev => {
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSidebarSelection = () => setSelectedSidebarIds(new Set());
  const handleSidebarBulkStatus = async (newStatus) => {
    if (selectedSidebarIds.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedSidebarIds);
      await bulkUpdateAnfragen(ids, newStatus);
      const label = newStatus === 'abgeschlossen' ? 'als erledigt markiert'
        : newStatus === 'in_bearbeitung' ? 'auf "In Bearbeitung" gesetzt'
        : 'wieder geöffnet';
      toast.success(`${ids.length} ${ids.length === 1 ? 'Anfrage' : 'Anfragen'} ${label}`);
      setSelectedSidebarIds(new Set());
      await loadAnfragen();
      if (selectedAnfrage && ids.includes(selectedAnfrage.id)) {
        const refreshed = await getAnfrageById(selectedAnfrage.id);
        setSelectedAnfrage(refreshed);
      }
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setBulkBusy(false);
    }
  };
  const handleSidebarBulkDelete = async () => {
    if (selectedSidebarIds.size === 0) return;
    const n = selectedSidebarIds.size;
    if (!window.confirm(`${n} ${n === 1 ? 'Anfrage' : 'Anfragen'} wirklich löschen?`)) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedSidebarIds);
      await bulkDeleteAnfragen(ids);
      toast.success(`${ids.length} ${ids.length === 1 ? 'Anfrage' : 'Anfragen'} gelöscht`);
      const hitSelected = selectedAnfrage && ids.includes(selectedAnfrage.id);
      setSelectedSidebarIds(new Set());
      await loadAnfragen();
      if (hitSelected) {
        const remaining = anfragen.filter(a => !ids.includes(a.id));
        if (remaining.length > 0) {
          setSelectedId(remaining[0].id);
          navigate(`/anfragen/${remaining[0].id}`, { replace: true });
        } else {
          setSelectedId(null);
          setSelectedAnfrage(null);
        }
      }
    } catch (err) {
      toast.error('Fehler beim Löschen');
    } finally {
      setBulkBusy(false);
    }
  };

  // Mobile drawer state
  const [showLeftDrawer, setShowLeftDrawer] = useState(false);
  const [showRightDrawer, setShowRightDrawer] = useState(false);
  // Linke Anfragen-Liste auf Desktop ein-/ausklappbar. Persistiert, damit die
  // Wahl auch nach Reload bleibt.
  const [leftCollapsed, setLeftCollapsed] = useState(() => {
    try { return localStorage.getItem('anfrageDetail.leftCollapsed') === '1'; } catch { return false; }
  });
  const [rightCollapsed, setRightCollapsed] = useState(() => {
    try { return localStorage.getItem('anfrageDetail.rightCollapsed') === '1'; } catch { return false; }
  });
  const toggleLeftCollapsed = () => {
    setLeftCollapsed(v => {
      const next = !v;
      try { localStorage.setItem('anfrageDetail.leftCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  const toggleRightCollapsed = () => {
    setRightCollapsed(v => {
      const next = !v;
      try { localStorage.setItem('anfrageDetail.rightCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Per-User anpassbare Spaltenbreiten. Default-Werte hängen vom orgType ab
  // (HeyKiki startet etwas kompakter, damit das Transkript mehr Platz hat),
  // aber jeder Nutzer kann per Drag am Spaltenrand nachjustieren.
  const SIDEBAR_MAX = 900;
  // Unterhalb dieser Ziehbreite wird die Sidebar als eingeklappt gerendert. Gleichzeitig
  // der Floor für expandierte Breiten — d.h. zwischen Threshold und Max kann frei
  // gezogen werden, erst darunter kippt die UI in die Collapse-Leiste.
  const AUTO_COLLAPSE_THRESHOLD = 100;
  // Collapsed-Strip-Breite muss mit dem w-10 am Render-Punkt übereinstimmen.
  const COLLAPSED_STRIP_WIDTH = 40;
  const clampWidth = (n) => Math.max(AUTO_COLLAPSE_THRESHOLD, Math.min(SIDEBAR_MAX, n));
  const defaultWidthFor = (side) => side === 'left'
    ? (isHeykikiOrg ? 240 : 280)
    : (isHeykikiOrg ? 260 : 320);
  const [leftWidth, setLeftWidth] = useState(() => {
    try {
      const saved = localStorage.getItem('anfrageDetail.leftWidth');
      if (saved) return clampWidth(parseInt(saved, 10));
    } catch {}
    return isHeykikiOrg ? 240 : 280;
  });
  const [rightWidth, setRightWidth] = useState(() => {
    try {
      const saved = localStorage.getItem('anfrageDetail.rightWidth');
      if (saved) return clampWidth(parseInt(saved, 10));
    } catch {}
    return isHeykikiOrg ? 260 : 320;
  });

  // Drag-to-resize. onMove aktualisiert State live, onUp persistiert den
  // finalen Wert. currentWidth wird in der Closure mitgeführt, weil State-
  // Updates asynchron sind und wir in onUp sonst einen veralteten Wert lesen.
  const startResize = useCallback((side, fromCollapsed = false) => (e) => {
    e.preventDefault();
    const startX = e.clientX;
    // Aus eingeklappt heraus: so tun, als wäre die Collapse-Leiste (40px) die
    // "Sidebar" — dann folgt der Sidebar-Rand während des Drags dem Cursor.
    // Ansonsten startet der Drag von der tatsächlichen Width.
    const savedWidth = side === 'left' ? leftWidth : rightWidth;
    const startWidth = fromCollapsed ? COLLAPSED_STRIP_WIDTH : savedWidth;
    let currentWidth = savedWidth;
    // Collapsed-Zustand während des Drags. Wechselt live mit der Mausposition —
    // Listener werden NICHT beim Collapse aufgeräumt, damit man mit gehaltener
    // Maustaste direkt wieder rauziehen kann.
    let isCollapsedDuringDrag = fromCollapsed;

    if (fromCollapsed) {
      if (side === 'left') setLeftCollapsed(false);
      else setRightCollapsed(false);
    }

    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      // Links: Ziehen nach rechts vergrößert. Rechts: Ziehen nach links vergrößert.
      const rawWidth = side === 'left' ? startWidth + delta : startWidth - delta;
      const shouldCollapse = rawWidth < AUTO_COLLAPSE_THRESHOLD;

      // Bidirektionaler Collapse/Expand-Switch während des Drags.
      if (shouldCollapse !== isCollapsedDuringDrag) {
        isCollapsedDuringDrag = shouldCollapse;
        if (side === 'left') setLeftCollapsed(shouldCollapse);
        else setRightCollapsed(shouldCollapse);
      }

      if (!shouldCollapse) {
        // Drag-Floor ist AUTO_COLLAPSE_THRESHOLD (nicht SIDEBAR_MIN) — erlaubt
        // sichtbares Weiter-Zudrücken bis knapp an die Collapse-Grenze.
        currentWidth = clampWidth(rawWidth);
        if (side === 'left') setLeftWidth(currentWidth);
        else setRightWidth(currentWidth);
      }
    };
    const onUp = () => {
      cleanup();
      try {
        localStorage.setItem(`anfrageDetail.${side}Collapsed`, isCollapsedDuringDrag ? '1' : '0');
      } catch {}
      if (isCollapsedDuringDrag) {
        // Auf Default zurück, damit der nächste Expand nicht auf Zwergenbreite öffnet.
        const fallback = defaultWidthFor(side);
        if (side === 'left') setLeftWidth(fallback);
        else setRightWidth(fallback);
        try { localStorage.setItem(`anfrageDetail.${side}Width`, String(fallback)); } catch {}
      } else {
        try { localStorage.setItem(`anfrageDetail.${side}Width`, String(currentWidth)); } catch {}
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftWidth, rightWidth, isHeykikiOrg]);

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Status update
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Bearbeiter-Zuweisung
  const [benutzerList, setBenutzerList] = useState([]);
  const [assigningBearbeiter, setAssigningBearbeiter] = useState(false);

  // KI Zusammenfassung
  const [summarizing, setSummarizing] = useState(false);

  // Skizze
  const [showSkizzenPad, setShowSkizzenPad] = useState(null); // { imageUrl? } oder null
  const [isSkizzeSaving, setIsSkizzeSaving] = useState(false);

  // Termin-Erstellen Modal (HeyKiki Quick-Action). Bewusst nicht per Navigate
  // auf /kalender — der Zustand "Anfrage offen" soll erhalten bleiben.
  const [showTerminForm, setShowTerminForm] = useState(false);
  const [terminSaving, setTerminSaving] = useState(false);
  // Neue-Anfrage-Modal direkt in der Detail-Ansicht — kein Redirect zur Liste.
  const [showNeueAnfrage, setShowNeueAnfrage] = useState(false);
  // Smooth Entry-Animation (analog zu Kundendetail). Kein framer-motion nötig —
  // ein Tailwind-Transition-Toggle über `mounted` reicht. Die Animation läuft
  // parallel zum initialen Paint, verlängert also nichts.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Current selected ID (from URL or first in list)
  const [selectedId, setSelectedId] = useState(id || null);

  // Sidebar-View: 'anfragen' (Default) oder 'aktionen' (Pending Notifications)
  const [sidebarMode, setSidebarMode] = useState('anfragen');
  const [offeneAktionen, setOffeneAktionen] = useState([]);
  const [offeneAktionenLoading, setOffeneAktionenLoading] = useState(false);

  // Load anfragen list
  const loadAnfragen = useCallback(async () => {
    try {
      setLoadingList(true);
      const data = await getAllAnfragen();
      setAnfragen(data);
    } catch (err) {
      console.error('Error loading anfragen:', err);
      toast.error('Fehler beim Laden der Anfragen');
    } finally {
      setLoadingList(false);
    }
  }, []);

  // Load offene Aktionen (Pending Notifications)
  const loadOffeneAktionen = useCallback(async () => {
    try {
      setOffeneAktionenLoading(true);
      const data = await getOffeneAktionen();
      setOffeneAktionen(data || []);
    } catch (err) {
      console.error('Error loading offene aktionen:', err);
    } finally {
      setOffeneAktionenLoading(false);
    }
  }, []);

  // Mitarbeiter-Liste für Zuweisung (einmalig laden)
  useEffect(() => {
    getAllBenutzer({ aktiv: 'true' })
      .then(data => setBenutzerList(data || []))
      .catch(err => console.error('Mitarbeiter-Liste konnte nicht geladen werden:', err));
  }, []);

  // Load single anfrage detail
  const loadAnfrageDetail = useCallback(async (anfrageId) => {
    if (!anfrageId) {
      setSelectedAnfrage(null);
      return;
    }
    try {
      setLoadingDetail(true);
      const data = await getAnfrageById(anfrageId);
      setSelectedAnfrage(data);
      // Glocke auto-clearen: alle Notifications zu dieser Anfrage als gelesen
      // markieren. Fire-and-forget — Anzeige-Update kommt beim nächsten
      // unread-count-Poll oder spätestens beim Bell-Klick. Fehler nicht-fatal.
      notificationApi.markReadByAnfrage(anfrageId).catch(err => {
        console.warn('Notification-Auto-Clear fehlgeschlagen:', err?.message);
      });
    } catch (err) {
      console.error('Error loading anfrage detail:', err);
      toast.error('Fehler beim Laden der Anfrage');
      setSelectedAnfrage(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadAnfragen();
    loadOffeneAktionen();
  }, [loadAnfragen, loadOffeneAktionen]);

  // When list loads, auto-select first if no ID in URL
  useEffect(() => {
    if (!loadingList && anfragen.length > 0 && !selectedId) {
      const firstId = anfragen[0].id;
      setSelectedId(firstId);
      navigate(`/anfragen/${firstId}`, { replace: true });
    }
  }, [loadingList, anfragen, selectedId, navigate]);

  // When URL param changes, update selectedId
  useEffect(() => {
    if (id && id !== selectedId) {
      setSelectedId(id);
    }
  }, [id]);

  // When selectedId changes, load detail
  useEffect(() => {
    if (selectedId) {
      loadAnfrageDetail(selectedId);
    }
  }, [selectedId, loadAnfrageDetail]);

  // Data refresh subscription
  useAnfragenRefresh(useCallback(() => {
    loadAnfragen();
    loadOffeneAktionen();
    if (selectedId) loadAnfrageDetail(selectedId);
  }, [loadAnfragen, loadOffeneAktionen, loadAnfrageDetail, selectedId]));

  // Handlers
  const handleSelect = (anfrageId) => {
    setSelectedId(anfrageId);
    navigate(`/anfragen/${anfrageId}`, { replace: true });
    if (isMobile) setShowLeftDrawer(false);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedAnfrage) return;
    try {
      setStatusUpdating(true);
      await updateAnfrage(selectedAnfrage.id, { status: newStatus });
      toast.success(`Status auf "${STATUS_CONFIG[newStatus]?.label || newStatus}" geändert`);
      await loadAnfrageDetail(selectedAnfrage.id);
      await loadAnfragen();
    } catch (err) {
      toast.error('Fehler beim Ändern des Status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAssignBearbeiter = async (bearbeiterId) => {
    if (!selectedAnfrage) return;
    try {
      setAssigningBearbeiter(true);
      // bearbeiterId="" → Zuweisung entfernen; sonst Benutzer-ID
      await updateAnfrage(selectedAnfrage.id, { bearbeiterId: bearbeiterId || null });
      const assigned = bearbeiterId ? benutzerList.find(b => b.id === bearbeiterId) : null;
      toast.success(assigned ? `Zugewiesen an ${assigned.name}` : 'Zuweisung entfernt');
      await loadAnfrageDetail(selectedAnfrage.id);
      await loadAnfragen();
    } catch (err) {
      toast.error('Fehler beim Zuweisen');
    } finally {
      setAssigningBearbeiter(false);
    }
  };

  const handleEditSave = async (formData) => {
    if (!selectedAnfrage) return;
    try {
      setEditSaving(true);
      await updateAnfrage(selectedAnfrage.id, formData);
      toast.success('Anfrage aktualisiert');
      setIsEditModalOpen(false);
      await loadAnfrageDetail(selectedAnfrage.id);
      await loadAnfragen();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAnfrage) return;
    try {
      setDeleting(true);
      await deleteAnfrage(selectedAnfrage.id);
      toast.success('Anfrage gelöscht');
      setDeleteConfirm(false);
      setSelectedAnfrage(null);
      const remaining = anfragen.filter(a => a.id !== selectedAnfrage.id);
      if (remaining.length > 0) {
        setSelectedId(remaining[0].id);
        navigate(`/anfragen/${remaining[0].id}`, { replace: true });
      } else {
        setSelectedId(null);
        navigate('/anfragen', { replace: true });
      }
      await loadAnfragen();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  // Schneller Delete direkt aus der Sidebar-Zeile (ohne Confirm-Modal),
  // mit einfacher window.confirm-Rückfrage.
  const handleDeleteFromList = async (anfrage) => {
    if (!anfrage?.id) return;
    const label = anfrage.betreff || 'diese Anfrage';
    if (!window.confirm(`„${label}" wirklich löschen?`)) return;
    try {
      await deleteAnfrage(anfrage.id);
      toast.success('Anfrage gelöscht');
      if (anfrage.id === selectedId) {
        const remaining = anfragen.filter(a => a.id !== anfrage.id);
        if (remaining.length > 0) {
          setSelectedId(remaining[0].id);
          navigate(`/anfragen/${remaining[0].id}`, { replace: true });
        } else {
          setSelectedId(null);
          setSelectedAnfrage(null);
        }
      }
      await loadAnfragen();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  // KI Zusammenfassung
  const handleZusammenfassung = async () => {
    if (!selectedAnfrage || summarizing) return;
    setSummarizing(true);
    try {
      await zusammenfassungAnfrage(selectedAnfrage.id);
      toast.success('KI Zusammenfassung erstellt');
      await loadAnfrageDetail(selectedAnfrage.id);
    } catch (err) {
      toast.error('Zusammenfassung fehlgeschlagen: ' + (err.response?.data?.error || err.message));
    } finally {
      setSummarizing(false);
    }
  };

  // Termin aus der Anfrage heraus anlegen. Der Dialog zeigt TerminForm im Modal
  // (analog ProjektDetail), mit Kunde und Ort aus der Anfrage vorbefüllt.
  const handleCreateTermin = async (formData) => {
    if (!selectedAnfrage) return;
    setTerminSaving(true);
    try {
      const payload = {
        ...formData,
        anfrageId: selectedAnfrage.id,
        kundeId: formData.kundeId || selectedAnfrage.kundeId || null,
      };
      await createTermin(payload);
      toast.success('Termin angelegt');
      setShowTerminForm(false);
      await loadAnfrageDetail(selectedAnfrage.id);
    } catch (err) {
      toast.error('Termin konnte nicht angelegt werden: ' + (err.response?.data?.error || err.message));
    } finally {
      setTerminSaving(false);
    }
  };

  // "Termin bestätigen"-Shortcut in der HeyKiki-Sidebar. Springt zum
  // TerminActionsBlock unter dem Transkript — dort entscheidet der User,
  // ob er Bestätigen / Alternative / Ablehnen wählt. Kein direkter Confirm,
  // weil Luca das bewusst als zweistufige UX haben möchte (offene Aufgaben
  // als Fenster unter dem Sprechblasen-Verlauf).
  const handleJumpToTerminAktionen = () => {
    const el = document.getElementById('termin-aktionen-block');
    if (!el) {
      toast('Keine offenen Termin-Aktionen gefunden.');
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-green-500');
    setTimeout(() => el.classList.remove('ring-2', 'ring-green-500'), 1800);
  };

  // Skizze speichern (als Foto zur Anfrage)
  const handleSaveSkizze = async (file) => {
    if (!selectedAnfrage) return;
    setIsSkizzeSaving(true);
    try {
      await uploadFoto(selectedAnfrage.kundeId, file, 'Skizze', selectedAnfrage.id);
      setShowSkizzenPad(null);
      toast.success('Skizze gespeichert');
      await loadAnfrageDetail(selectedAnfrage.id);
    } catch (err) {
      toast.error('Fehler beim Speichern der Skizze: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSkizzeSaving(false);
    }
  };

  const channel = selectedAnfrage ? detectChannel(selectedAnfrage) : 'manuell';

  // ── Render ──────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      {/* Lucas/Husmann&Dreier-Feedback 2026-05-07: volle `container mx-auto`-
          Breite analog Kundenliste/Mitarbeiter (1536px max), damit links
          deutlich nach links rückt. Die "abgehakt"-Wirkung der ersten
          Iteration war keine Container-Sache — wird über mehr Innen-
          Padding + Spacing in den Sektionen behandelt, nicht über engeren
          Container. */}
      <div className="container mx-auto px-4 sm:px-6 w-full h-[calc(100dvh-120px)] sm:h-[calc(100dvh-140px)] flex flex-col">

        {/* Mobile Header Bar */}
        {isMobile && (
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-surface shrink-0">
            <button
              onClick={() => setShowLeftDrawer(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <PanelLeftOpen className="w-5 h-5 text-text-muted" />
            </button>
            <div className="flex-1 min-w-0">
              {selectedAnfrage ? (
                <p className="text-sm font-medium truncate">{selectedAnfrage.betreff}</p>
              ) : (
                <p className="text-sm text-text-muted">Anfrage auswählen</p>
              )}
            </div>
            {selectedAnfrage && (
              <button
                onClick={() => setShowRightDrawer(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <PanelRightOpen className="w-5 h-5 text-text-muted" />
              </button>
            )}
          </div>
        )}

        {/* Main 3-Column Layout — Staggered Entry-Animation analog zu
            Kundendetail: linke Sidebar schiebt sich von links rein, Mitte
            fadet sanft ein, rechte Sidebar schiebt sich von rechts rein.
            Die Staffelung läuft über Tailwind-Delays auf die inneren
            Wrapper, nicht auf dieses äußere Element — daher hier kein
            Translate mehr.
            Lucas/Husmann&Dreier-Feedback 2026-05-07: Das ganze 3-Spalten-
            Block sitzt jetzt als Card auf dem grauen Page-Background —
            rounded-xl + Border + leichter Shadow, damit der Übergang
            grau→weiß nicht hart erscheint. */}
        <div className="flex-1 flex min-h-0 my-2 rounded-xl border border-border shadow-sm overflow-hidden bg-surface">

          {/* Left Sidebar - Desktop (ein-/ausklappbar + resizable) */}
          {!isMobile && !leftCollapsed && (
            <>
              <div
                style={{
                  width: leftWidth,
                  transitionProperty: 'opacity, transform',
                  transitionDuration: '300ms',
                  transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)'
                }}
                className={`border-r border-border bg-surface shrink-0 flex flex-col min-h-0 ${
                  mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                }`}
              >
                <AnfragenSidebar
                  anfragen={anfragen}
                  selectedId={selectedId}
                  onSelect={handleSelect}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  loading={loadingList}
                  onCollapse={toggleLeftCollapsed}
                  benutzerList={benutzerList}
                  bearbeiterFilter={bearbeiterFilter}
                  onBearbeiterFilterChange={handleBearbeiterFilterChange}
                  currentUserId={currentUser?.id}
                  showExtras={leftWidth >= SIDEBAR_MAX * 0.6}
                  bulkEnabled={leftWidth >= SIDEBAR_MAX * 0.6}
                  sortBy={sortBy}
                  onSortByChange={handleSortByChange}
                  onDeleteAnfrage={handleDeleteFromList}
                  selectedIds={selectedSidebarIds}
                  onToggleSelect={toggleSidebarSelect}
                  onToggleSelectAll={toggleSidebarSelectAll}
                  onClearSelection={clearSidebarSelection}
                  onBulkStatusChange={handleSidebarBulkStatus}
                  onBulkDelete={handleSidebarBulkDelete}
                  bulkBusy={bulkBusy}
                  onNeueAnfrage={() => setShowNeueAnfrage(true)}
                  sidebarMode={sidebarMode}
                  onSidebarModeChange={setSidebarMode}
                  offeneAktionen={offeneAktionen}
                  offeneAktionenLoading={offeneAktionenLoading}
                />
              </div>
              {/* Drag-Handle: 4px breite Hit-Area, optisch unauffällig, färbt beim Hover */}
              <div
                onMouseDown={startResize('left')}
                className="w-1 shrink-0 cursor-col-resize hover:bg-primary/40 transition-colors relative z-10"
                title="Zum Anpassen ziehen"
              />
            </>
          )}
          {!isMobile && leftCollapsed && (
            <div className="w-10 border-r border-border bg-surface shrink-0 flex flex-col items-center py-3 relative">
              {/* Drag-Griff: aus dem eingeklappten Zustand nach rechts ziehen = ausklappen */}
              <div
                onMouseDown={startResize('left', true)}
                className="absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/40 transition-colors"
                title="Zum Ausklappen ziehen"
              />
              <button
                onClick={toggleLeftCollapsed}
                className="p-1.5 hover:bg-gray-100 rounded-md transition"
                title="Anfragen-Liste ausklappen"
              >
                <ChevronRight className="w-4 h-4 text-text-muted" />
              </button>
            </div>
          )}

          {/* Middle Column.
              Wichtig gegen Flackern beim Anfrage-Wechsel: Der bisherige
              ChannelContent bleibt stehen und wird mit Opacity weich auf den
              neuen Zustand überblendet. Nur beim allerersten Laden (noch gar
              keine Anfrage im Memory) zeigen wir den zentrierten Spinner.
              Zusätzlich: Mount-Fade mit kleinem Delay (Stagger hinter der
              linken Sidebar). */}
          <div
            style={{
              transitionProperty: 'opacity, transform',
              transitionDuration: '300ms',
              transitionDelay: '75ms',
              transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)'
            }}
            className={`flex-1 flex flex-col min-w-0 min-h-0 bg-surface relative ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            {loadingDetail && !selectedAnfrage ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              // `key` auf die Anfrage-ID zwingt React zu einem Remount der
              // darunter liegenden View beim Anfrage-Wechsel, damit der interne
              // Zustand (aufgeklappte Transkripte / Sub-Tabs) sauber pro Anfrage
              // zurückgesetzt wird. KEINE `animate-fade-in` mehr: die 300ms-
              // Einblendung lief bei jedem Klick neu und ließ den bereits
              // geladenen Content künstlich „nachladen" wirken. Jetzt erscheint
              // der Inhalt sofort, sobald die Daten da sind.
              <div
                key={selectedAnfrage?.id || 'empty'}
                className="flex-1 flex flex-col min-h-0"
              >
                <ChannelContent anfrage={selectedAnfrage} channel={channel} onSkizze={(imageUrl) => setShowSkizzenPad({ imageUrl: imageUrl || null })} />
              </div>
            )}
            {loadingDetail && selectedAnfrage && (
              <div className="absolute top-3 right-3 pointer-events-none">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
              </div>
            )}
          </div>

          {/* Right Sidebar - Desktop (ein-/ausklappbar + resizable) */}
          {!isMobile && !rightCollapsed && (
            <>
              <div
                onMouseDown={startResize('right')}
                className="w-1 shrink-0 cursor-col-resize hover:bg-primary/40 transition-colors relative z-10"
                title="Zum Anpassen ziehen"
              />
              <div
                style={{
                  width: rightWidth,
                  transitionProperty: 'opacity, transform',
                  transitionDuration: '300ms',
                  transitionDelay: '150ms',
                  transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)'
                }}
                className={`border-l border-border bg-surface shrink-0 flex flex-col min-h-0 ${
                  mounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
              >
                <DetailPanel
                  anfrage={selectedAnfrage}
                  onEdit={() => setIsEditModalOpen(true)}
                  onStatusChange={handleStatusChange}
                  onDelete={() => setDeleteConfirm(true)}
                  statusUpdating={statusUpdating}
                  onZusammenfassung={handleZusammenfassung}
                  summarizing={summarizing}
                  onSkizze={(imageUrl) => setShowSkizzenPad({ imageUrl: imageUrl || null })}
                  onTerminErstellen={() => setShowTerminForm(true)}
                  onTerminBestaetigen={handleJumpToTerminAktionen}
                  benutzerList={benutzerList}
                  onAssignBearbeiter={handleAssignBearbeiter}
                  assigningBearbeiter={assigningBearbeiter}
                  onCollapse={toggleRightCollapsed}
                  onRefresh={async () => { if (selectedAnfrage?.id) { await loadAnfrageDetail(selectedAnfrage.id); await loadAnfragen(); await loadOffeneAktionen(); } }}
                />
              </div>
            </>
          )}
          {!isMobile && rightCollapsed && (
            <div className="w-10 border-l border-border bg-surface shrink-0 flex flex-col items-center py-3 relative">
              {/* Drag-Griff: aus dem eingeklappten Zustand nach links ziehen = ausklappen */}
              <div
                onMouseDown={startResize('right', true)}
                className="absolute top-0 left-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/40 transition-colors"
                title="Zum Ausklappen ziehen"
              />
              <button
                onClick={toggleRightCollapsed}
                className="p-1.5 hover:bg-gray-100 rounded-md transition"
                title="Detail-Panel einblenden"
              >
                <ChevronLeft className="w-4 h-4 text-text-muted" />
              </button>
            </div>
          )}
        </div>

        {/* Mobile Left Drawer */}
        {isMobile && showLeftDrawer && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowLeftDrawer(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-[300px] bg-surface shadow-xl flex flex-col transition-transform duration-200">
              <div className="flex items-center justify-between p-3 border-b border-border">
                <h3 className="text-sm font-semibold">Anfragen</h3>
                <button onClick={() => setShowLeftDrawer(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <AnfragenSidebar
                  anfragen={anfragen}
                  selectedId={selectedId}
                  onSelect={handleSelect}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  loading={loadingList}
                  benutzerList={benutzerList}
                  bearbeiterFilter={bearbeiterFilter}
                  onBearbeiterFilterChange={handleBearbeiterFilterChange}
                  currentUserId={currentUser?.id}
                  showExtras={true}
                  bulkEnabled={true}
                  sortBy={sortBy}
                  onSortByChange={handleSortByChange}
                  onDeleteAnfrage={handleDeleteFromList}
                  selectedIds={selectedSidebarIds}
                  onToggleSelect={toggleSidebarSelect}
                  onToggleSelectAll={toggleSidebarSelectAll}
                  onClearSelection={clearSidebarSelection}
                  onBulkStatusChange={handleSidebarBulkStatus}
                  onBulkDelete={handleSidebarBulkDelete}
                  bulkBusy={bulkBusy}
                  onNeueAnfrage={() => { setShowLeftDrawer(false); setShowNeueAnfrage(true); }}
                  sidebarMode={sidebarMode}
                  onSidebarModeChange={setSidebarMode}
                  offeneAktionen={offeneAktionen}
                  offeneAktionenLoading={offeneAktionenLoading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Mobile Right Drawer */}
        {isMobile && showRightDrawer && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowRightDrawer(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-[320px] bg-surface shadow-xl flex flex-col transition-transform duration-200">
              <div className="flex items-center justify-between p-3 border-b border-border">
                <h3 className="text-sm font-semibold">Details</h3>
                <button onClick={() => setShowRightDrawer(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <DetailPanel
                  anfrage={selectedAnfrage}
                  onEdit={() => { setIsEditModalOpen(true); setShowRightDrawer(false); }}
                  onStatusChange={(s) => { handleStatusChange(s); setShowRightDrawer(false); }}
                  onDelete={() => { setDeleteConfirm(true); setShowRightDrawer(false); }}
                  statusUpdating={statusUpdating}
                  onZusammenfassung={() => { handleZusammenfassung(); setShowRightDrawer(false); }}
                  summarizing={summarizing}
                  onSkizze={(imageUrl) => { setShowSkizzenPad({ imageUrl: imageUrl || null }); setShowRightDrawer(false); }}
                  onTerminErstellen={() => { setShowTerminForm(true); setShowRightDrawer(false); }}
                  onTerminBestaetigen={() => { setShowRightDrawer(false); setTimeout(handleJumpToTerminAktionen, 250); }}
                  benutzerList={benutzerList}
                  onAssignBearbeiter={handleAssignBearbeiter}
                  assigningBearbeiter={assigningBearbeiter}
                  onRefresh={async () => { if (selectedAnfrage?.id) { await loadAnfrageDetail(selectedAnfrage.id); await loadAnfragen(); await loadOffeneAktionen(); } }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Anfrage bearbeiten"
        size="md"
      >
        {selectedAnfrage && (
          <AnfrageForm
            anfrage={selectedAnfrage}
            onSubmit={handleEditSave}
            onCancel={() => setIsEditModalOpen(false)}
            isLoading={editSaving}
          />
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        title="Anfrage löschen"
        size="sm"
      >
        <p className="text-sm text-text mb-4">
          Möchtest du die Anfrage <strong>"{selectedAnfrage?.betreff}"</strong> wirklich löschen?
          Alle zugehörigen Daten (E-Mails, KVAs, etc.) werden ebenfalls gelöscht.
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => setDeleteConfirm(false)}
            className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition"
          >
            Abbrechen
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 rounded-lg transition disabled:opacity-50"
          >
            {deleting ? 'Wird gelöscht...' : 'Löschen'}
          </button>
        </div>
      </Modal>

      {/* SkizzenPad */}
      {showSkizzenPad && (
        <SkizzenPad
          onSave={handleSaveSkizze}
          onClose={() => setShowSkizzenPad(null)}
          saving={isSkizzeSaving}
          initialImageUrl={showSkizzenPad.imageUrl || null}
        />
      )}

      {/* Termin erstellen (HeyKiki Quick-Action) */}
      <Modal
        isOpen={showTerminForm}
        onClose={() => setShowTerminForm(false)}
        title="Termin erstellen"
        size="lg"
      >
        {selectedAnfrage && (
          <TerminForm
            termin={{
              titel: selectedAnfrage.betreff || '',
              ort: selectedAnfrage.kunde?.adresse || '',
              beschreibung: selectedAnfrage.nachricht || ''
            }}
            preselectedKunde={selectedAnfrage.kunde || null}
            onSubmit={handleCreateTermin}
            onCancel={() => setShowTerminForm(false)}
            isLoading={terminSaving}
          />
        )}
      </Modal>

      {/* Neue Anfrage erstellen — direkt aus der Sidebar, springt nach Erstellung
          in die neue Anfrage, statt wegzunavigieren. */}
      <NeueAnfrageModal
        isOpen={showNeueAnfrage}
        onClose={() => setShowNeueAnfrage(false)}
        onCreated={async (newAnfrage) => {
          await loadAnfragen();
          if (newAnfrage?.id) {
            setSelectedId(newAnfrage.id);
            navigate(`/anfragen/${newAnfrage.id}`, { replace: false });
          }
        }}
      />
    </Layout>
  );
}
